import { useEffect, useMemo, useState, useRef } from "react";
import * as React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ArrowRight, Loader2, Plus, Trash2, Save, Mail, Phone,
  ExternalLink, Check, CreditCard, Banknote, Building2,
  FileText, Link as LinkIcon, Users, UserPlus, X,
  ChevronDown, ChevronUp, Calendar, Pencil, Receipt,
  TrendingUp, TrendingDown, Wallet, DollarSign, Upload,
  Crown, AlertTriangle, RotateCcw, CalendarRange,
  Layers, GitBranch,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { FullFrameLogo, ffGradient, ffGradientText } from "./FullFrameLogo";
import { ProductionScheduleDialog } from "./ProductionScheduleDialog";
import { ProductionCrewDialog } from "./ProductionCrewDialog";
import { SendToAllDialog } from "./SendToAllDialog";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { UploadButtons } from "./UploadButtons";
import { PaperlessSend } from "./PaperlessSend";
import { useUndo, UndoRedoFab } from "./UndoStack";
import { FF_ROLES, FF_VAT_RATE } from "./constants";
import {
  getProduction, listProductions, addExpenseRow, updateExpenseRow, deleteExpenseRow,
  updateProduction, resolveCompanyIdBySlug,
  uploadAdditionalExpense, updateAdditionalExpense, deleteAdditionalExpense,
  getFullFrameConfig, searchSuppliers,
  addPayment, updatePayment, deletePayment,
  addContact, updateContact, deleteContact,
  uploadQuoteFile, uploadQuoteLink, deleteQuote,
  addQuoteFile, renameQuoteFile, deleteQuoteFile,
  addCrewPayment, deleteCrewPayment, replaceCrewPaymentInvoice,
  mintShareLink, createProduction,
} from "./api";

// Phone numbers of the two agency partners — used both by the partner
// crown badge on crew rows AND by the dashboard's partner-earnings card.
// Backend source of truth: routes/fullframe.py::PARTNER_DEFS.
const PARTNER_PHONES = {
  "0508851145": { key: "daniel", name: "דניאל חסין" },
  "0503357357": { key: "nir", name: "ניר דדון" },
};

// Owner spec 2026-06-15j — storage URLs are now stored RELATIVE
// (e.g. "/api/fullframe/storage/<prod>/crew-pay-<id>.jpg"). When rendering
// them in <a href> / <img src> we have to prepend REACT_APP_BACKEND_URL.
// Legacy data may still contain a full https:// URL — pass those through.
const _BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
function resolveStorageUrl(url) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("/api/")) return `${_BACKEND_URL}${url}`;
  return url;
}
function _normalizePhone(s) {
  return String(s || "").replace(/\D+/g, "");
}
function detectPartnerByPhone(phone) {
  const d = _normalizePhone(phone);
  if (!d) return null;
  for (const [partnerPhone, info] of Object.entries(PARTNER_PHONES)) {
    const pd = _normalizePhone(partnerPhone);
    if (d === pd || d.slice(-9) === pd.slice(-9)) return info;
  }
  return null;
}

const ils = (n) => new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 2 }).format(Number(n) || 0);
const ils0 = (n) => new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(Number(n) || 0);

// Convert local Israeli phone (e.g. 0501234567 / 050-123-4567 / +972 50 123 4567)
// to the international E.164-without-plus form `wa.me` expects.
const toIntlPhone = (p) => {
  const digits = String(p || "").replace(/[^\d+]/g, "");
  if (!digits) return "";
  if (digits.startsWith("+")) return digits.slice(1);
  if (digits.startsWith("972")) return digits;
  if (digits.startsWith("0")) return "972" + digits.slice(1);
  return digits;
};

export default function FullFrameProductionRouter() {
  return <FullFrameProduction />;
}

function FullFrameProduction() {
  const { slug, prodId } = useParams();
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(null);
  const [config, setConfig] = useState({ paperless_wa_phone: "972437488824", vat_rate: FF_VAT_RATE });
  const [prod, setProd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingHeader, setSavingHeader] = useState(false);
  // Modal popups for Quote + Additional contacts; inline toggle for Crew table.
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false); // owner spec 2026-07-02
  const [crewSheetOpen, setCrewSheetOpen] = useState(false); // owner spec 2026-07-06 — דף קשר
  const [sendAllOpen, setSendAllOpen] = useState(false); // owner spec 2026-02-08 — FlowUpSender broadcast
  const [showCrew, setShowCrew] = useState(false);
  const [addSubOpen, setAddSubOpen] = useState(false); // owner spec 2026-10-12 — תת הפקה
  const [subProductions, setSubProductions] = useState([]);

  const refreshSubProductions = async (cid, parentId) => {
    try {
      const all = await listProductions(cid);
      setSubProductions(all.filter((sp) => sp.parent_production_id === parentId));
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const cid = await resolveCompanyIdBySlug(slug);
        setCompanyId(cid);
        const [p, cfg] = await Promise.all([
          getProduction(prodId, cid),
          getFullFrameConfig().catch(() => null),
        ]);
        setProd(p);
        if (cfg) setConfig(cfg);
        if (p.is_multi_production) {
          await refreshSubProductions(cid, p.id);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "ההפקה לא נמצאה");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, prodId]);

  const totals = useMemo(() => {
    const rows = prod?.expenses || [];
    const additional = prod?.additional_expenses || [];
    const payments = prod?.payments || [];
    const totalAmount = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const totalVat = rows.reduce((s, r) => s + (Number(r.vat_total) || 0), 0);
    const totalPaid = rows.reduce((s, r) => s + (Number(r.amount_paid) || 0), 0);
    const additionalTotal = additional.reduce((s, r) => s + (Number(r.total_with_vat) || 0), 0);
    const additionalVat = additional.reduce((s, r) => {
      const pre = Number(r.pre_vat) || 0;
      const tot = Number(r.total_with_vat) || 0;
      return s + Math.max(0, tot - pre);
    }, 0);
    const vatCredit = totalVat + additionalVat;        // VAT we paid on inputs
    const grand = totalAmount + totalVat + additionalTotal;
    const paidIncome = payments.reduce((s, p) => s + (p.paid ? (Number(p.amount) || 0) : 0), 0);
    // The "agreed price + VAT" that the client owes total
    const baseIncome = Number(prod?.income) || 0;
    const productionCost = baseIncome * 1.18;
    // VAT collected on PAID income only (fits user's rule "no income until paid")
    // Paid amounts are inclusive of VAT already, so VAT-portion = paid * 18/118.
    const vatCollected = paidIncome * (0.18 / 1.18);
    // Net VAT to remit. Positive → owed to tax authority. Negative → cashback to us.
    const vatNet = vatCollected - vatCredit;
    return {
      totalAmount, totalVat, totalPaid, additionalTotal, additionalVat,
      vatCredit, grand, paidIncome,
      productionCost,
      vatCollected, vatNet,
      // Net before income tax = (paid income WITHOUT VAT) − (expenses WITHOUT VAT)
      // = paidIncome − vatCollected − (grand − vatCredit)
      // = paidIncome − grand − vatNet
      // For 11800 paid / 0 expenses: 11800 − 0 − 1800 = 10000 ✓
      profitBeforeTax: paidIncome - grand - vatNet,
    };
  }, [prod]);

  const refresh = async () => setProd(await getProduction(prodId, companyId));

  // Global undo wiring — owner spec 2026-06-14b: every data-changing
  // action pushes an entry onto the stack so the floating Undo/Redo
  // FAB at the top-left can revert/replay. Max depth is 100.
  const { pushUndo } = useUndo();

  const onAddRow = async () => {
    try {
      const r = await addExpenseRow(prodId, { role: "", provider: "", amount: 0, vat_total: 0, amount_paid: 0 }, companyId);
      const newRowId = r?.id;
      await refresh();
      if (newRowId) {
        pushUndo({
          label: "שורת צוות חדשה",
          undo: async () => { try { await deleteExpenseRow(prodId, newRowId, companyId); } catch (_e) { /* ignore — undo is best-effort */ } await refresh(); },
          redo: async () => { await addExpenseRow(prodId, { role: "", provider: "", amount: 0, vat_total: 0, amount_paid: 0 }, companyId); await refresh(); },
        });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "שגיאה בהוספת שורה"); }
  };

  const onUpdateRow = async (rowId, patch) => {
    // PATCH carries ONLY the changed field. Backend uses exclude_unset.
    // BONUS: when "amount" changes we ALSO send vat_total = amount * 18%
    // (auto-VAT). User can still override the VAT cell manually afterwards.
    const payload = {};
    if ("role" in patch) payload.role = patch.role || "";
    if ("provider" in patch) payload.provider = patch.provider || "";
    if ("provider_phone" in patch) payload.provider_phone = patch.provider_phone || "";
    if ("amount_paid" in patch) payload.amount_paid = Number(patch.amount_paid) || 0;
    if ("amount" in patch) {
      const amt = Number(patch.amount) || 0;
      payload.amount = amt;
      payload.vat_total = Math.round(amt * FF_VAT_RATE * 100) / 100;
    }
    if ("vat_total" in patch) payload.vat_total = Number(patch.vat_total) || 0;
    // Capture prev state for undo (only fields we're about to change)
    const prevRow = (prod?.expenses || []).find((r) => r.id === rowId);
    const prevPayload = prevRow ? Object.keys(payload).reduce((acc, k) => { acc[k] = prevRow[k]; return acc; }, {}) : null;
    try {
      await updateExpenseRow(prodId, rowId, payload, companyId);
      await refresh();
      if (prevPayload) {
        const changedKeys = Object.keys(payload).filter((k) => prevPayload[k] !== payload[k]);
        if (changedKeys.length) {
          pushUndo({
            label: `עריכת שורת צוות (${changedKeys.join(", ")})`,
            undo: async () => { await updateExpenseRow(prodId, rowId, prevPayload, companyId); await refresh(); },
            redo: async () => { await updateExpenseRow(prodId, rowId, payload, companyId); await refresh(); },
          });
        }
      }
    }
    catch (e) { toast.error(e.response?.data?.detail || "שגיאה בעדכון"); }
  };

  const onDeleteRow = async (rowId) => {
    if (!window.confirm("למחוק את השורה?")) return;
    // Snapshot the row so we can re-create it on undo (best-effort —
    // payments[] history is lost on backend delete, but the row body
    // is fully restorable).
    const snap = (prod?.expenses || []).find((r) => r.id === rowId) || null;
    try {
      await deleteExpenseRow(prodId, rowId, companyId);
      await refresh();
      if (snap) {
        pushUndo({
          label: `מחיקת שורת צוות "${snap.provider || snap.role || "—"}"`,
          undo: async () => {
            await addExpenseRow(prodId, {
              role: snap.role || "", provider: snap.provider || "",
              provider_phone: snap.provider_phone || "",
              amount: Number(snap.amount) || 0, vat_total: Number(snap.vat_total) || 0,
              amount_paid: Number(snap.amount_paid) || 0,
              has_vat: !!snap.has_vat, paid: !!snap.paid,
            }, companyId);
            await refresh();
          },
          redo: async () => { try { await deleteExpenseRow(prodId, rowId, companyId); } catch (_e) { /* ignore — undo is best-effort */ } await refresh(); },
        });
      }
    }
    catch (e) { toast.error(e.response?.data?.detail || "שגיאה במחיקה"); }
  };

  const onSaveHeader = async (patch) => {
    setSavingHeader(true);
    try {
      // ─── Single source of truth: `client_budget` ─────────────────
      // Older productions stored the project budget in the legacy
      // `income` field. To keep them in sync (so payments section and
      // summary always read the same value), we mirror the new budget
      // into `income` whenever the client file is saved. The user's
      // explicit rule (2026-05-20): "כל מקום שרשום תקציב פרויקט
      // משתנה אוטומטית בתשלום מהלקוח וגם בסיכום פרויקט".
      const mergedPatch = { ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, "client_budget")) {
        mergedPatch.income = Number(patch.client_budget) || 0;
      }
      const updated = await updateProduction(prodId, mergedPatch, companyId);
      setProd(updated);
      toast.success("נשמר");
    } catch (e) { toast.error(e.response?.data?.detail || "שגיאה בשמירה"); }
    finally { setSavingHeader(false); }
  };

  if (loading) return <div className="min-h-screen bg-stone-800 text-stone-100 flex items-center justify-center" dir="rtl"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!prod) return <div className="min-h-screen bg-stone-800 text-stone-100 flex items-center justify-center" dir="rtl"><Link to={`/${slug}`} className="text-pink-400">חזרה לדשבורד</Link></div>;

  // ── Multi-production: dedicated simplified view ──────────────────────
  if (prod.is_multi_production) {
    return (
      <div className="min-h-screen bg-stone-800 text-stone-100" dir="rtl">
        <Toaster position="top-center" richColors dir="rtl" theme="dark" />
        <header className="relative z-10 border-b border-white/10 bg-stone-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
            <Link to={`/${slug}`} className="inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition" data-testid="ff-back-link">
              <ArrowRight className="h-4 w-4" />
              <span className="text-sm">דשבורד</span>
            </Link>
            <FullFrameLogo size="sm" />
          </div>
        </header>
        <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          <MultiProductionView
            prod={prod}
            companyId={companyId}
            subProductions={subProductions}
            onAddSub={() => setAddSubOpen(true)}
            navigate={navigate}
            slug={slug}
            onSaveHeader={onSaveHeader}
          />
          <AddSubProductionDialog
            open={addSubOpen}
            onOpenChange={setAddSubOpen}
            parentProd={prod}
            companyId={companyId}
            onCreated={(child) => setSubProductions((arr) => [...arr, child])}
          />
        </main>
        <div className="fixed top-20 left-4 z-[10100]">
          <UndoRedoFab />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-800 text-stone-100" dir="rtl">
      <Toaster position="top-center" richColors dir="rtl" theme="dark" />

      {/* Solid uniform grey backdrop — no decorative haloes per user request. */}

      <header className="relative z-10 border-b border-white/10 bg-stone-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to={`/${slug}`} className="inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition" data-testid="ff-back-link">
            <ArrowRight className="h-4 w-4" />
            <span className="text-sm">דשבורד</span>
          </Link>
          <FullFrameLogo size="sm" />
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Production Header */}
        <ProductionHeader prod={prod} companyId={companyId} onSave={onSaveHeader} saving={savingHeader} />

        {/* Sub-production quick-add button — visible when this is NOT already a sub-production */}
        {!prod.parent_production_id && (
          <div className="flex justify-end">
            <Button
              onClick={() => setAddSubOpen(true)}
              size="sm"
              className="bg-fuchsia-600/80 hover:bg-fuchsia-500 text-white font-bold gap-1.5 border border-fuchsia-400/30 shadow-sm"
              data-testid="ff-prod-add-sub-btn"
            >
              <GitBranch className="h-3.5 w-3.5" />
              הוסף תת הפקה
            </Button>
          </div>
        )}

        {/* AddSubProductionDialog */}
        <AddSubProductionDialog
          open={addSubOpen}
          onOpenChange={setAddSubOpen}
          parentProd={prod}
          companyId={companyId}
        />

        {/* Quick contact actions — UNIFIED dark style with one accent per channel.
            Only WhatsApp keeps the iconic green (universal recognition). */}
        <ContactBar
          phone={prod.client_phone}
          whatsapp={prod.client_whatsapp || prod.client_phone}
          email={prod.client_email}
          name={prod.client_name || prod.name}
          contactName={prod.contact_name}
        />

        {/* Compact action cards — "הצעת מחיר" + "אנשי קשר נוספים" + "צוות הפקה".
            Each is a pill button — quote+contacts open popups, crew toggles
            the inline expenses table below. */}
        <CompactActionCards
          quoteCount={(prod.quote_files || []).length || (prod.quote_url ? 1 : 0)}
          contactsCount={(prod.additional_contacts || []).length}
          crewCount={(prod.expenses || []).length}
          scheduleCount={(prod.schedule || []).length}
          crewSheetCount={(prod.crew_roster || []).length}
          showCrew={showCrew}
          onOpenQuote={() => setQuoteOpen(true)}
          onOpenContacts={() => setContactsOpen(true)}
          onOpenSchedule={() => setScheduleOpen(true)}
          onOpenCrewSheet={() => setCrewSheetOpen(true)}
          onToggleCrew={() => setShowCrew((s) => !s)}
        />

        {/* Owner spec 2026-02-08 — bulk broadcast via FlowUpSender.
            Placed as a full-width pill so it can't be missed. */}
        <button
          type="button"
          onClick={() => setSendAllOpen(true)}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-l from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-black font-extrabold text-sm px-4 py-2.5 shadow-lg shadow-emerald-500/20 transition"
          data-testid="ff-open-send-all"
        >
          <WhatsAppIcon className="h-4 w-4" />
          שלח הודעה לכולם ({((prod.crew_roster || []).filter((r) => r.phone).length) + ((prod.additional_contacts || []).filter((c) => c.phone).length)})
        </button>

        {/* Crew (צוות הפקה) — inline collapsible expense table, same as before */}
        {showCrew && (
          <CrewExpensesSection
            prod={prod}
            companyId={companyId}
            onAdd={onAddRow}
            onUpdate={onUpdateRow}
            onDelete={onDeleteRow}
            onRefresh={refresh}
          />
        )}

        {/* Additional Expenses Table — receipt photos + OCR + Paperless WA.
            Per image_5, this lives ABOVE the payment tracker. */}
        <AdditionalExpensesSection
          prod={prod}
          companyId={companyId}
          paperlessPhone={config.paperless_wa_phone}
          onChange={refresh}
        />

        {/* Payments split tracker — track cash/credit/transfer chunks of the income */}
        <PaymentsSection
          prod={prod}
          companyId={companyId}
          onChange={refresh}
        />

        {/* Bottom totals — 5-rubric structured summary. */}
        <ProductionSummaryTable
          totals={totals}
          budget={Number(prod.client_budget) || Number(prod.income) || 0}
          budgetIncludesVat={!!prod.client_budget_includes_vat}
          paidToDate={(prod?.payments || []).reduce((s, p) => s + (p.paid ? (Number(p.amount) || 0) : 0), 0)}
          profitSplit={prod.profit_split || {}}
          onProfitSplitChange={(next) => onSaveHeader({ profit_split: next })}
        />
      </main>

      {/* Quote files popup */}
      {quoteOpen && (
        <QuoteFilesModal
          prod={prod}
          companyId={companyId}
          onClose={() => setQuoteOpen(false)}
          onChanged={refresh}
        />
      )}

      {/* Additional contacts popup */}
      {contactsOpen && (
        <ContactsModal
          prod={prod}
          companyId={companyId}
          onClose={() => setContactsOpen(false)}
          onChanged={refresh}
        />
      )}

      {/* Owner spec 2026-07-02 — Schedule + equipment planner */}
      <ProductionScheduleDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        production={prod}
        companyId={companyId}
        onSaved={refresh}
      />

      {/* Owner spec 2026-07-06 — Crew roster (דף קשר) */}
      <ProductionCrewDialog
        open={crewSheetOpen}
        onOpenChange={setCrewSheetOpen}
        production={prod}
        companyId={companyId}
        onSaved={refresh}
      />

      {/* Owner spec 2026-02-08 — FlowUpSender broadcast */}
      <SendToAllDialog
        open={sendAllOpen}
        onOpenChange={setSendAllOpen}
        production={prod}
      />

      {/* Floating undo/redo controls — instant, client-side, no reloads.
          Higher z than modals so undo works while a modal is open. */}
      <div className="fixed top-20 left-4 z-[10100]">
        <UndoRedoFab />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// ShareLinkButton — owner spec 2026-07-08l
// -----------------------------------------------------------------------
function ShareLinkButton({ production, companyId }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");

  const buildUrl = (token) => `${window.location.origin}/share/production/${token}`;

  const openLink = async (regenerate = false) => {
    if (!production?.id) return;
    setBusy(true);
    try {
      const { token } = await mintShareLink(production.id, companyId, regenerate);
      const full = buildUrl(token);
      setUrl(full);
      setOpen(true);
      try {
        await navigator.clipboard.writeText(full);
        toast.success("הלינק הועתק");
      } catch { /* copy failed silently */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "לא הצלחתי ליצור לינק");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => openLink(false)}
        disabled={busy}
        className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold gap-1.5"
        data-testid="ff-share-link-btn"
      >
        {busy ? "⏳" : "🔗"}
        לינק ציבורי
      </Button>
      {open && (
        <div className="fixed inset-0 z-[10200] flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div
            dir="rtl"
            className="bg-stone-900 border border-white/15 rounded-2xl p-5 max-w-lg w-full text-white space-y-3 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="ff-share-link-dialog"
          >
            <div className="flex items-center gap-2">
              <span className="text-2xl">🔗</span>
              <h3 className="text-xl font-extrabold bg-gradient-to-l from-emerald-300 to-teal-300 bg-clip-text text-transparent">
                לינק ציבורי לצפייה
              </h3>
            </div>
            <p className="text-sm text-stone-300 leading-snug">
              כל אחד עם הלינק הזה יראה את פרטי ההפקה בקריאה בלבד — לוח זמנים, דף קשר, שעות ולוקיישן. אף אחד לא יוכל לערוך.
            </p>
            <div className="flex items-center gap-2 bg-black/40 border border-white/15 rounded-lg p-2">
              <input
                type="text"
                readOnly
                value={url}
                className="flex-1 bg-transparent text-emerald-200 text-sm outline-none text-left"
                dir="ltr"
                onFocus={(e) => e.target.select()}
                data-testid="ff-share-link-url"
              />
              <button
                type="button"
                onClick={async () => { try { await navigator.clipboard.writeText(url); toast.success("הועתק"); } catch { toast.error("נכשל"); } }}
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs px-3 py-1 rounded-md"
                data-testid="ff-share-link-copy"
              >
                העתק
              </button>
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => openLink(true)}
                disabled={busy}
                className="text-amber-300 hover:text-amber-200 text-xs font-bold"
                data-testid="ff-share-link-regen"
              >
                🔄 יצירה מחדש (מבטל לינק ישן)
              </button>
              <div className="flex gap-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`הלינק להפקה "${production?.name || ""}":\n${url}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                  data-testid="ff-share-link-wa"
                >
                  שלח בוואטסאפ
                </a>
                <Button
                  onClick={() => setOpen(false)}
                  className="bg-stone-700 hover:bg-stone-600 text-white text-xs font-bold"
                  data-testid="ff-share-link-close"
                >
                  סגור
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// =====================================================================
//   ADD SUB-PRODUCTION DIALOG — owner spec 2026-10-12
// =====================================================================
// Opened from inside an existing production page via the "הוסף תת הפקה"
// button.  Creates a new production with parent_production_id = current
// production's id and inherits the client info from the parent.
function AddSubProductionDialog({ open, onOpenChange, parentProd, companyId, onCreated }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [budget, setBudget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setName(""); setBudget(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("נדרש שם לתת ההפקה"); return; }
    setBusy(true);
    try {
      const child = await createProduction({
        name: name.trim(),
        client_name: parentProd.client_name || "",
        contact_name: parentProd.contact_name || "",
        client_phone: parentProd.client_phone || "",
        client_email: parentProd.client_email || "",
        client_whatsapp: parentProd.client_whatsapp || parentProd.client_phone || "",
        client_budget: Number(budget) || 0,
        parent_production_id: parentProd.id,
      }, companyId);
      toast.success(`תת הפקה "${child.name}" נוצרה`);
      onOpenChange(false);
      if (onCreated) {
        onCreated(child);
      } else {
        // legacy: navigate to the new sub-production
        setTimeout(() => navigate(`../${child.id}`, { relative: "path" }), 800);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה ביצירה");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[10100] flex items-center justify-center bg-black/70 p-4 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      onClick={() => onOpenChange(false)}
      data-testid="ff-add-sub-dialog-overlay"
    >
      <div
        dir="rtl"
        className="bg-stone-900 border border-fuchsia-500/30 rounded-2xl p-6 max-w-md w-full text-white shadow-2xl space-y-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="ff-add-sub-dialog"
      >
        <div className="flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-fuchsia-400" />
          <h3 className="text-xl font-extrabold text-fuchsia-200">הוסף תת הפקה</h3>
        </div>
        <p className="text-sm text-stone-400 leading-snug">
          תת ההפקה תקושר אוטומטית להפקה <strong className="text-white">{parentProd?.name}</strong> ותירש את פרטי הלקוח.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-stone-300 mb-1 block">שם תת ההפקה *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="פרק א׳ / יום הצילומים"
              autoFocus
              required
              data-testid="ff-add-sub-name"
              className="w-full bg-stone-800 border border-fuchsia-500/30 rounded-lg px-3 py-2 text-white placeholder-stone-500 focus:outline-none focus:border-fuchsia-400 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-stone-300 mb-1 block">תקציב תת הפקה (₪)</label>
            <input
              type="number"
              step="0.01"
              dir="ltr"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="25,000"
              data-testid="ff-add-sub-budget"
              className="w-full bg-stone-800 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-stone-500 focus:outline-none focus:border-fuchsia-400 text-right text-sm"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              disabled={busy}
              className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-bold"
              data-testid="ff-add-sub-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "צור תת הפקה"}
            </Button>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="border-white/10 text-stone-300 hover:text-white"
              data-testid="ff-add-sub-cancel"
            >
              ביטול
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =====================================================================
//   MULTI-PRODUCTION VIEW — dedicated layout for is_multi_production
// =====================================================================
const ils_mp = (n) => new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(Number(n) || 0);

function MultiProductionView({ prod, companyId, subProductions, onAddSub, navigate, slug, onSaveHeader }) {
  const totalBudget = Number(prod.client_budget) || 0;
  const allocatedBudget = subProductions.reduce((s, sp) => s + (Number(sp.client_budget) || 0), 0);
  const remainingBudget = totalBudget - allocatedBudget;
  const isOverBudget = totalBudget > 0 && allocatedBudget > totalBudget;

  // Aggregate totals for ProductionSummaryTable
  const aggTotals = subProductions.reduce((acc, sp) => {
    const rows = sp.expenses || [];
    const addRows = sp.additional_expenses || [];
    acc.totalAmount    += rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    acc.totalVat       += rows.reduce((s, r) => s + (Number(r.vat_total) || 0), 0);
    acc.additionalTotal += addRows.reduce((s, r) => s + (Number(r.total_with_vat) || 0), 0);
    acc.additionalVat  += addRows.reduce((s, r) => s + (Number(r.vat_total) || 0), 0);
    return acc;
  }, { totalAmount: 0, totalVat: 0, additionalTotal: 0, additionalVat: 0 });

  const aggBudget = subProductions.reduce((s, sp) => s + (Number(sp.client_budget) || 0), 0);
  const aggPaid   = subProductions.reduce((s, sp) =>
    s + (sp.payments || []).filter((p) => p.paid).reduce((ps, p) => ps + (Number(p.amount) || 0), 0), 0);
  const anyVat    = subProductions.some((sp) => !!sp.client_budget_includes_vat);

  return (
    <div className="space-y-6" data-testid="ff-multi-prod-view">
      {/* Production title + badge */}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight">{prod.name}</h1>
        <span className="inline-flex items-center gap-1 text-xs font-bold bg-pink-500/20 border border-pink-500/40 text-pink-300 rounded-full px-2.5 py-1">
          <Layers className="h-3.5 w-3.5" />
          הפקה מרובה
        </span>
      </div>

      {/* Client info header */}
      <div
        className="rounded-2xl border border-pink-500/30 bg-pink-950/20 p-4 flex flex-wrap items-center gap-6"
        data-testid="ff-multi-client-header"
      >
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-pink-300/70 mb-0.5">שם מזמין</div>
          <div className="text-base font-bold text-white">{prod.client_name || "—"}</div>
        </div>
        {prod.client_phone && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-pink-300/70 mb-0.5">טלפון</div>
            <div className="text-base font-bold text-white" dir="ltr">{prod.client_phone}</div>
          </div>
        )}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-pink-300/70 mb-0.5">תקציב כולל</div>
          <div className="text-base font-extrabold text-fuchsia-200 tabular-nums">{ils_mp(totalBudget)}</div>
        </div>
      </div>

      {/* Sub-productions section */}
      <div className="space-y-3" data-testid="ff-multi-sub-section">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-bold text-stone-200 text-sm">
            תת הפקות <span className="text-stone-400">({subProductions.length})</span>
          </h2>
          <button
            type="button"
            onClick={onAddSub}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-fuchsia-600/80 hover:bg-fuchsia-500 text-white text-sm font-bold border border-fuchsia-400/30 shadow-sm transition"
            data-testid="ff-multi-add-sub-btn"
          >
            <Plus className="h-4 w-4" />
            הוסף תת הפקה
          </button>
        </div>

        {subProductions.length === 0 ? (
          <div
            className="py-12 flex flex-col items-center gap-3 border border-dashed border-white/10 rounded-2xl text-stone-500"
            data-testid="ff-multi-empty"
          >
            <GitBranch className="h-10 w-10 opacity-30" />
            <p className="text-sm">עוד אין תת הפקות. לחץ על "הוסף תת הפקה" כדי להתחיל.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {subProductions.map((sp) => (
              <SubProductionCard key={sp.id} sp={sp} navigate={navigate} slug={slug} />
            ))}
          </div>
        )}
      </div>

      {/* Budget summary */}
      {totalBudget > 0 && (
        <div
          className="rounded-2xl border border-white/10 bg-stone-800/60 p-4 space-y-3"
          data-testid="ff-multi-budget-summary"
        >
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-stone-400">סיכום תקציב</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-xl bg-stone-700/60">
              <div className="text-[10px] text-stone-400 font-bold mb-1">תקציב כולל</div>
              <div className="text-lg font-extrabold text-white tabular-nums">{ils_mp(totalBudget)}</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-amber-950/40 border border-amber-500/20">
              <div className="text-[10px] text-stone-400 font-bold mb-1">הוקצה לתת הפקות</div>
              <div className="text-lg font-extrabold text-amber-300 tabular-nums">{ils_mp(allocatedBudget)}</div>
            </div>
            <div className={`text-center p-3 rounded-xl border ${isOverBudget ? "bg-rose-950/40 border-rose-500/30" : "bg-emerald-950/40 border-emerald-500/20"}`}>
              <div className="text-[10px] text-stone-400 font-bold mb-1">נותר</div>
              <div className={`text-lg font-extrabold tabular-nums ${isOverBudget ? "text-rose-300" : "text-emerald-300"}`}>
                {ils_mp(remainingBudget)}
              </div>
            </div>
          </div>
          {isOverBudget && (
            <div className="flex items-center gap-2 text-rose-300 text-xs font-bold">
              <AlertTriangle className="h-3.5 w-3.5" />
              חריגה מהתקציב הכולל
            </div>
          )}
        </div>
      )}

      {/* Aggregate project summary — same as ProductionSummaryTable but for all sub-productions */}
      {subProductions.length > 0 && (
        <ProductionSummaryTable
          totals={aggTotals}
          budget={aggBudget}
          budgetIncludesVat={anyVat}
          paidToDate={aggPaid}
          profitSplit={prod.profit_split || {}}
          onProfitSplitChange={(next) => onSaveHeader && onSaveHeader({ profit_split: next })}
        />
      )}
    </div>
  );
}

function SubProductionCard({ sp, navigate, slug }) {
  const expenseRows = sp.expenses || [];
  const additionalRows = sp.additional_expenses || [];
  const totalExp = expenseRows.reduce((s, r) => s + (Number(r.amount) || 0) + (Number(r.vat_total) || 0), 0)
    + additionalRows.reduce((s, r) => s + (Number(r.total_with_vat) || 0), 0);
  const budget = Number(sp.client_budget) || 0;
  const netBeforeTax = budget - totalExp;

  return (
    <div
      className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-950/20 hover:border-fuchsia-400/60 transition p-4 space-y-3"
      data-testid={`ff-sub-prod-card-${sp.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h4 className="font-bold text-white truncate">{sp.name}</h4>
          <div className="flex items-center gap-1 mt-0.5">
            <GitBranch className="h-3 w-3 text-fuchsia-400" />
            <span className="text-[10px] text-fuchsia-300/70 font-bold">תת הפקה</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/${slug}/productions/${sp.id}`)}
          className="inline-flex items-center gap-1 text-xs font-bold text-fuchsia-300 hover:text-fuchsia-100 bg-fuchsia-500/20 hover:bg-fuchsia-500/35 border border-fuchsia-500/40 rounded-lg px-2.5 py-1.5 transition shrink-0"
          data-testid={`ff-sub-prod-open-${sp.id}`}
        >
          פתח
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-stone-800/60 px-2.5 py-2">
          <div className="text-[10px] text-stone-400 font-bold">תקציב</div>
          <div className="text-sm font-extrabold text-white tabular-nums">{ils_mp(budget)}</div>
        </div>
        <div className="rounded-lg bg-stone-800/60 px-2.5 py-2">
          <div className="text-[10px] text-stone-400 font-bold">הוצאות</div>
          <div className="text-sm font-extrabold text-rose-300 tabular-nums">{ils_mp(totalExp)}</div>
        </div>
      </div>
      {budget > 0 && (
        <div className={`rounded-lg px-2.5 py-2 flex items-center justify-between border ${netBeforeTax >= 0 ? "bg-emerald-950/40 border-emerald-500/20" : "bg-rose-950/40 border-rose-500/20"}`}>
          <span className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">יתרה לפני מס</span>
          <span className={`text-sm font-extrabold tabular-nums ${netBeforeTax >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
            {ils_mp(netBeforeTax)}
          </span>
        </div>
      )}
    </div>
  );
}

// ----------- Production Header (editable) -----------
function ProductionHeader({ prod, companyId, onSave, saving }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({ ...prod });
  useEffect(() => { setDraft({ ...prod }); }, [prod.id]);

  if (!edit) {
    return (
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-400 font-bold">הפקה</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold mt-1 text-white" data-testid="ff-prod-name">{prod.name}</h1>
          {prod.client_name && <p className="text-stone-300 mt-2">לקוח: <span className="text-white font-semibold">{prod.client_name}</span></p>}
        </div>
        <div className="flex items-center gap-2">
          <ShareLinkButton production={prod} companyId={companyId} />
          <Button onClick={() => setEdit(true)} variant="outline" className="border-white/10 bg-white/5 hover:bg-white/10 text-white" data-testid="ff-edit-header">
            ערוך פרטי לקוח
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-stone-700 p-5 space-y-3" data-testid="ff-edit-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FFInput label="שם הפקה" value={draft.name || ""} onChange={(v) => setDraft({ ...draft, name: v })} testid="ff-edit-name" />
        <FFInput label="שם החברה" value={draft.client_name || ""} onChange={(v) => setDraft({ ...draft, client_name: v })} testid="ff-edit-client" />
        <FFInput label="שם איש הקשר" value={draft.contact_name || ""} onChange={(v) => setDraft({ ...draft, contact_name: v })} testid="ff-edit-contact-name" />
        <div>
          <FFInput label="תקציב פרויקט (₪)" type="number" value={draft.client_budget || 0} onChange={(v) => setDraft({ ...draft, client_budget: v })} testid="ff-edit-budget" />
          <label className="mt-1.5 flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!draft.client_budget_includes_vat}
              onChange={(e) => setDraft({ ...draft, client_budget_includes_vat: e.target.checked })}
              className="h-3.5 w-3.5 accent-fuchsia-500"
              data-testid="ff-edit-budget-vat"
            />
            <span className="text-[11px] text-stone-300">פלוס מע&quot;מ 18%</span>
          </label>
        </div>
        <FFInput label="טלפון" value={draft.client_phone || ""} onChange={(v) => setDraft({ ...draft, client_phone: v })} testid="ff-edit-phone" />
        <FFInput label="WhatsApp (לא חובה)" value={draft.client_whatsapp || ""} onChange={(v) => setDraft({ ...draft, client_whatsapp: v })} testid="ff-edit-wa" />
        <FFInput label="אימייל" type="email" value={draft.client_email || ""} onChange={(v) => setDraft({ ...draft, client_email: v })} testid="ff-edit-email" />
      </div>
      <div className="flex items-center gap-2 justify-end">
        <Button variant="ghost" onClick={() => { setEdit(false); setDraft({ ...prod }); }} className="text-stone-300 hover:bg-white/5" data-testid="ff-edit-cancel">ביטול</Button>
        <Button
          onClick={async () => { await onSave({ ...draft, client_budget: Number(draft.client_budget) || 0, client_budget_includes_vat: !!draft.client_budget_includes_vat }); setEdit(false); }}
          disabled={saving}
          className={`${ffGradient} text-white font-bold`}
          data-testid="ff-edit-save"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 ml-1.5" />שמור</>}
        </Button>
      </div>
    </div>
  );
}

function FFInput({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs text-stone-300 mb-1 block">{label}</label>
      <Input
        type={type}
        dir={type === "email" || type === "number" ? "ltr" : "rtl"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-stone-800/60 border-white/10 text-white"
        data-testid={testid}
      />
    </div>
  );
}

// ----------- Quick contact bar — unified dark style -----------
function ContactBar({ phone, whatsapp, email, name, contactName }) {
  const norm = (p) => (p || "").replace(/[^\d+]/g, "");
  const wa = toIntlPhone(whatsapp);
  // Call button shows the contact's first name when known — "התקשר ערן".
  const callLabel = contactName ? `התקשר ${contactName.split(" ")[0]}` : "התקשר";
  const items = [
    {
      key: "wa", label: "WhatsApp", iconEl: <WhatsAppIcon size={22} />,
      href: wa ? `https://wa.me/${wa}?text=${encodeURIComponent(`היי ${contactName || name || ""}`)}` : null,
    },
    {
      key: "email", label: "אימייל", iconEl: <Mail className="h-5 w-5 text-pink-400" />,
      href: email ? `mailto:${email}` : null,
    },
    {
      key: "phone", label: callLabel, iconEl: <Phone className="h-5 w-5 text-pink-400" />,
      href: phone ? `tel:${norm(phone)}` : null,
    },
  ];
  return (
    <section className="grid grid-cols-3 gap-3" data-testid="ff-contact-bar">
      {items.map((it) => {
        const disabled = !it.href;
        return (
          <a
            key={it.key}
            href={it.href || "#"}
            target={it.key === "wa" ? "_blank" : undefined}
            rel={it.key === "wa" ? "noreferrer" : undefined}
            onClick={(e) => { if (disabled) e.preventDefault(); }}
            className={`relative flex items-center justify-center gap-2 rounded-xl py-3.5 sm:py-4 px-3 font-semibold text-white text-sm sm:text-base bg-stone-700 border border-white/10 transition-colors ${
              disabled ? "opacity-30 pointer-events-none" : "hover:bg-stone-800/80 hover:border-pink-500/30"
            }`}
            data-testid={`ff-contact-${it.key}`}
          >
            {it.iconEl}
            {it.label}
          </a>
        );
      })}
    </section>
  );
}

// ----------- Collapsible section wrapper -----------
// Reusable card with a clickable header (title + chevron). Defaults open.
// Used to wrap טבלת הוצאות נוספות / תשלום מהלקוח / טבלת סיכום פרויקט.
function CollapsibleSection({ title, icon: Icon, subtitle, headerExtra, defaultOpen = false, testid, accent = "pink", children }) {
  const [open, setOpen] = useState(defaultOpen);
  const accentColors = {
    pink: "text-pink-400",
    emerald: "text-emerald-400",
    sky: "text-sky-400",
  };
  return (
    <section className="rounded-2xl border border-white/10 bg-stone-700 overflow-hidden" data-testid={testid}>
      <div className="select-none">
        {/* Title row — always one line with icon, title & chevron. Click anywhere
            on this row (except the headerExtra below) toggles open/closed. */}
        <div
          onClick={() => setOpen((s) => !s)}
          className="flex items-center justify-between gap-3 cursor-pointer px-4 sm:px-5 py-3.5 hover:bg-white/[0.03] transition"
          data-testid={`${testid}-toggle`}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {Icon && <Icon className={`h-5 w-5 flex-shrink-0 ${accentColors[accent] || accentColors.pink}`} />}
            <div className="min-w-0">
              <h2 className="font-heading text-xl sm:text-2xl font-bold truncate">{title}</h2>
              {subtitle && <p className="text-xs text-stone-400 mt-0.5 truncate">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen((s) => !s); }}
            className="h-9 w-9 flex-shrink-0 inline-flex items-center justify-center rounded-lg text-stone-300 hover:bg-white/10 transition"
            aria-label={open ? "סגור" : "פתח"}
            data-testid={`${testid}-chevron`}
          >
            {open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </div>
        {/* Extras row — always under the title (mobile-friendly, desktop OK too) */}
        {headerExtra && (
          <div
            className="px-4 sm:px-5 pb-3 flex flex-wrap items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {headerExtra}
          </div>
        )}
      </div>
      {open && <div className="px-4 sm:px-5 pb-5 space-y-3">{children}</div>}
    </section>
  );
}


// ----------- Pill — solid filled card per tone -----------
function Pill({ label, value, tone, highlight }) {
  const palette = {
    positive: { bg: "bg-emerald-950 border-emerald-600/60", text: "text-emerald-200" },
    negative: { bg: "bg-rose-950 border-rose-600/60", text: "text-rose-200" },
    muted: { bg: "bg-stone-700 border-white/10", text: "text-stone-100" },
    info: { bg: "bg-sky-950 border-sky-600/60", text: "text-sky-200" },
    brand: { bg: "bg-pink-950 border-pink-500/70", text: "text-pink-200" },
  }[tone] || { bg: "bg-stone-700 border-white/10", text: "text-stone-100" };
  return (
    <div className={`rounded-xl border p-4 ${palette.bg} ${highlight ? "shadow-lg shadow-pink-500/20" : ""}`}>
      <div className="text-[11px] uppercase tracking-wider text-white font-bold">{label}</div>
      <div className={`mt-1.5 font-extrabold text-xl sm:text-2xl tabular-nums ${palette.text}`}>{value}</div>
    </div>
  );
}

// ----------- Expense Row (editable) -----------
function ExpenseRow({ row, prodId, companyId, onUpdate, onDelete, onRefresh }) {
  const [draft, setDraft] = useState({
    role: row.role || "", provider: row.provider || "", provider_phone: row.provider_phone || "",
    provider_whatsapp: row.provider_whatsapp || row.provider_phone || "",
    amount_paid: row.amount_paid ?? 0, amount: row.amount ?? 0, vat_total: row.vat_total ?? 0,
    // Default ✗ — no VAT (owner's rule 2026-05-20: עוסק פטור מקרה ברירת מחדל).
    // Existing rows that were saved with has_vat=true still display correctly.
    has_vat: row.has_vat === true,
    paid: !!row.paid,
  });
  const [payOpen, setPayOpen] = useState(false);
  useEffect(() => {
    setDraft({
      role: row.role || "", provider: row.provider || "", provider_phone: row.provider_phone || "",
      provider_whatsapp: row.provider_whatsapp || row.provider_phone || "",
      amount_paid: row.amount_paid ?? 0, amount: row.amount ?? 0, vat_total: row.vat_total ?? 0,
      has_vat: row.has_vat === true,
      paid: !!row.paid,
    });
    // Re-sync whenever ANY field changes — this is what makes
    // Undo/Redo actually visible: when the parent refreshes prod data
    // after an undo, the new row props flow in and the input fields
    // reflect the reverted values. Watching `row.id` alone misses
    // mid-row edits (owner bug-report 2026-06-15: "Undo נראה שלא עובד").
  }, [row.id, row.role, row.provider, row.provider_phone, row.amount, row.vat_total, row.amount_paid, row.has_vat, row.paid]);

  // Derived totals — sum of payment installments (preferred over the
  // legacy single `amount_paid` field, which the popup keeps in sync).
  const installments = Array.isArray(row.payments) ? row.payments : [];
  const installmentsTotal = installments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  // Prefer installmentsTotal whenever any installment exists; otherwise
  // fall back to the legacy `amount_paid` value so older rows still
  // show the right number until the user logs their first payment.
  const totalPaid = installments.length > 0 ? installmentsTotal : Number(row.amount_paid || 0);
  const grossTotal = (Number(draft.amount) || 0) + (Number(draft.vat_total) || 0);
  const remaining = Math.max(0, grossTotal - totalPaid);
  const fullyPaid = grossTotal > 0 && totalPaid + 0.01 >= grossTotal;
  // Overpayment — owner spec 2026-06-15c: when totalPaid exceeds the
  // gross, label the row in RED inside the paid pill ("תשלום יתר לספק")
  // along with the excess amount.
  const overpaid = grossTotal > 0 && totalPaid > grossTotal + 0.5;
  const overpayAmount = overpaid ? totalPaid - grossTotal : 0;

  const flush = (patch = {}) => {
    const next = { ...draft, ...patch };
    // Recompute VAT whenever amount OR has_vat changes
    const amt = Number(("amount" in patch) ? patch.amount : draft.amount) || 0;
    const hasVat = ("has_vat" in patch) ? patch.has_vat : draft.has_vat;
    if ("amount" in patch || "has_vat" in patch) {
      next.vat_total = hasVat ? Math.round(amt * FF_VAT_RATE * 100) / 100 : 0;
    }
    setDraft(next);
    // Send the recomputed VAT along when either field flips
    if ("amount" in patch || "has_vat" in patch) {
      onUpdate({ ...patch, vat_total: next.vat_total });
    } else {
      onUpdate(patch);
    }
  };

  // Pick a saved supplier — autofill name+phone in one go.
  const onPickSupplier = (s) => {
    setDraft((d) => ({ ...d, provider: s.name, provider_phone: s.phone }));
    // Two PATCHes (name+phone) — ok, race-free since each updates one field.
    onUpdate({ provider: s.name });
    onUpdate({ provider_phone: s.phone || "" });
  };

  const phoneIntl = toIntlPhone(draft.provider_phone);
  const phoneTel = String(draft.provider_phone || "").replace(/[^\d+]/g, "");

  // Partner badge: SA marks one of the two agency partners as crew —
  // identify them ONLY by phone (owner spec 2026-06-15) so a name typo
  // doesn't break the badge.
  const partnerInfo = detectPartnerByPhone(draft.provider_phone);

  return (
    <>
    <tr data-testid={`ff-row-${row.id}`} className="hover:bg-white/[0.02]">
      <td className="px-3 py-2.5">
        <Select
          value={draft.role || ""}
          onValueChange={(v) => { setDraft({ ...draft, role: v }); onUpdate({ role: v }); }}
        >
          <SelectTrigger
            className="w-full bg-stone-700 border border-white/15 hover:bg-stone-600 focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 text-white font-semibold h-10 text-base"
            data-testid={`ff-row-role-${row.id}`}
          >
            <SelectValue placeholder="בחר תפקיד" />
          </SelectTrigger>
          <SelectContent className="bg-stone-900 border-white/10 text-stone-100 max-h-72">
            {FF_ROLES.map((r) => (
              <SelectItem key={r} value={r} className="focus:bg-pink-500/10 focus:text-white text-base">{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2.5">
        <SupplierAutocomplete
          value={draft.provider}
          companyId={companyId}
          onChange={(v) => setDraft({ ...draft, provider: v })}
          onCommit={(v) => flush({ provider: v })}
          onPick={onPickSupplier}
          testid={`ff-row-provider-${row.id}`}
        />
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          {/* Crown badge — partner detected by the phone number on this
              row (owner spec 2026-06-15b). Appears on the RIGHT side
              (in RTL the badge is first in DOM order). */}
          {partnerInfo && (
            <span
              className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-amber-500/20 border border-amber-400/40 text-amber-300 shrink-0"
              data-testid={`ff-row-partner-crown-${row.id}`}
              title={`שותף: ${partnerInfo.name} — סכום השורה הזו נכלל בכרטיס הרווחים שלו בדשבורד`}
            >
              <Crown className="h-3.5 w-3.5" />
            </span>
          )}
          <input
            type="tel"
            dir="ltr"
            inputMode="tel"
            value={draft.provider_phone}
            onChange={(e) => setDraft({ ...draft, provider_phone: e.target.value })}
            onBlur={(e) => flush({ provider_phone: e.target.value })}
            placeholder="050…"
            className="flex-1 min-w-0 bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-right tabular-nums text-base"
            data-testid={`ff-row-phone-${row.id}`}
          />
          {/* "שמור" — explicit save button (owner spec 2026-06-15b). Persists
              the phone immediately so the partner-detection crown shows
              without waiting for blur. Highlights green once partner
              detected so the SA sees the link visually. */}
          <button
            type="button"
            onClick={() => flush({ provider_phone: draft.provider_phone })}
            className={`p-2 rounded-lg border text-xs font-bold transition shrink-0 ${
              partnerInfo
                ? "bg-amber-500/20 border-amber-400/60 text-amber-200 hover:bg-amber-500/30"
                : "bg-sky-500/10 border-sky-500/40 text-sky-200 hover:bg-sky-500/20"
            }`}
            data-testid={`ff-row-phone-save-${row.id}`}
            title={partnerInfo ? `זוהה שותף: ${partnerInfo.name}` : "שמור טלפון וזהה שותף"}
          >
            <Save className="h-4 w-4" />
          </button>
          <a
            href={phoneIntl ? `https://wa.me/${phoneIntl}` : "#"}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => { if (!phoneIntl) e.preventDefault(); }}
            className={`p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition ${!phoneIntl ? "opacity-30 pointer-events-none" : ""}`}
            data-testid={`ff-row-wa-${row.id}`}
            aria-label="ווטסאפ"
            title="פתח ווטסאפ"
          >
            <WhatsAppIcon size={18} />
          </a>
          <a
            href={phoneTel ? `tel:${phoneTel}` : "#"}
            onClick={(e) => { if (!phoneTel) e.preventDefault(); }}
            className={`p-2 rounded-lg bg-pink-500/10 border border-pink-500/30 text-pink-300 hover:bg-pink-500/20 transition ${!phoneTel ? "opacity-30 pointer-events-none" : ""}`}
            data-testid={`ff-row-call-${row.id}`}
            aria-label="התקשר"
            title="התקשר"
          >
            <Phone className="h-4 w-4" />
          </a>
        </div>
      </td>
      <td className="px-2 py-2.5">
        {/* 18% VAT toggle — default ✗ (owner's rule 2026-05-20: עוסק
            פטור הוא ברירת מחדל). Ticked = supplier has VAT, vat_total
            recomputed from net amount. */}
        <label className="flex items-center justify-center gap-1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!draft.has_vat}
            onChange={(e) => {
              const hasVat = e.target.checked;
              // Re-derive gross/vat from the current AMOUNT (net) so the
              // total cell stays correct. We treat draft.amount as the
              // canonical net value here.
              const net = Number(draft.amount) || 0;
              const vat = hasVat ? Math.round(net * FF_VAT_RATE * 100) / 100 : 0;
              setDraft({ ...draft, has_vat: hasVat, vat_total: vat });
              onUpdate({ has_vat: hasVat, vat_total: vat });
            }}
            className="h-4 w-4 accent-amber-500"
            data-testid={`ff-row-has-vat-${row.id}`}
            title='18% מע״מ — סמן ✓ עבור ספק עם מע״מ'
          />
          <span className="text-[10px] text-amber-300 font-bold whitespace-nowrap">+ מע״מ 18%</span>
        </label>
      </td>
      <td className="px-3 py-2.5 min-w-[200px]">
        {/* Single combined "עלות צוות הפקה + מע״מ" cell. When has_vat is true,
            the user enters GROSS (incl. VAT). When false (עוסק פטור),
            the value IS the total (no VAT split). */}
        <input
          type="number" step="0.01" dir="ltr"
          value={Math.round(((Number(draft.amount) || 0) + (Number(draft.vat_total) || 0)) * 100) / 100}
          onChange={(e) => {
            const gross = Number(e.target.value) || 0;
            if (draft.has_vat) {
              const net = Math.round((gross / 1.18) * 100) / 100;
              const vat = Math.round((gross - net) * 100) / 100;
              setDraft({ ...draft, amount: net, vat_total: vat });
            } else {
              setDraft({ ...draft, amount: gross, vat_total: 0 });
            }
          }}
          onBlur={(e) => {
            const gross = Number(e.target.value) || 0;
            if (draft.has_vat) {
              const net = Math.round((gross / 1.18) * 100) / 100;
              const vat = Math.round((gross - net) * 100) / 100;
              flush({ amount: net, vat_total: vat });
            } else {
              flush({ amount: gross, vat_total: 0 });
            }
          }}
          className={`w-full border font-bold focus:outline-none focus:ring-2 rounded-lg px-3 py-2 text-right tabular-nums text-base font-semibold transition-colors ${
            fullyPaid
              ? "bg-emerald-950 border-emerald-500/60 text-emerald-200 focus:ring-emerald-400/50 focus:border-emerald-400/60"
              : "bg-rose-950 border-rose-500/50 text-rose-200 focus:ring-pink-500/50 focus:border-pink-500/50"
          }`}
          data-testid={`ff-row-total-${row.id}`}
        />
        <input type="hidden" value={draft.vat_total} readOnly data-testid={`ff-row-vat-${row.id}`} />
      </td>
      <td className="px-3 py-2.5">
        {/* Paid pill — owner spec 2026-06-15: TWO-CELL layout —
            right side (in RTL) shows "שולם ₪X", left side shows
            "נותר ₪Y". When fullyPaid → both cells turn emerald-green
            with a centred "✓ שולם". When nothing paid → muted gray.
            When OVERPAID → entire pill turns red with the excess. */}
        <div
          className={`min-w-[180px] w-full h-11 rounded-lg overflow-hidden font-extrabold text-[11px] sm:text-xs transition border-2 whitespace-nowrap flex items-center ${
            overpaid
              ? "bg-rose-600/30 border-rose-400/70 text-rose-100 animate-pulse"
              : fullyPaid
              ? "bg-emerald-500/20 border-emerald-400/60 text-emerald-200"
              : totalPaid > 0
              ? "bg-amber-500/15 border-amber-400/50 text-amber-200"
              : "bg-stone-800/60 border-stone-500/40 text-stone-400"
          }`}
          data-testid={`ff-row-paid-state-${row.id}`}
          title={overpaid
            ? `תשלום יתר לספק: ₪${overpayAmount.toFixed(0)} (סה״כ שולם ${totalPaid.toFixed(0)} מתוך ${grossTotal.toFixed(0)})`
            : fullyPaid
            ? `שולם במלואו (₪${totalPaid.toFixed(0)})`
            : `שולם ₪${totalPaid.toFixed(0)} · נותר ₪${remaining.toFixed(0)}`}
        >
          {overpaid ? (
            <span className="flex-1 text-center px-2 leading-tight" data-testid={`ff-row-overpay-${row.id}`}>
              תשלום יתר לספק<br/>₪{overpayAmount.toFixed(0)}
            </span>
          ) : fullyPaid ? (
            <span className="flex-1 text-center px-2">✓ שולם במלואו</span>
          ) : (
            <>
              <span className="flex-1 text-center px-2 border-l border-current/30" data-testid={`ff-row-paid-amount-${row.id}`}>
                שולם<br/>₪{totalPaid.toFixed(0)}
              </span>
              <span className="flex-1 text-center px-2" data-testid={`ff-row-paid-remaining-${row.id}`}>
                נותר<br/>₪{remaining.toFixed(0)}
              </span>
            </>
          )}
        </div>
      </td>
      <td className="px-2 py-2.5">
        <div className="flex items-center justify-center gap-1">
          {/* "+" — open the crew payment popup. Lives next to the trash
              icon per owner spec 2026-06-14. */}
          <button
            type="button"
            onClick={() => setPayOpen(true)}
            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition rounded-lg p-2"
            data-testid={`ff-row-pay-open-${row.id}`}
            aria-label="הוסף תשלום"
            title="רישום תשלום לאיש צוות"
          >
            <Plus className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition rounded-lg p-2"
            data-testid={`ff-row-delete-${row.id}`}
            aria-label="מחק שורה"
            title="מחיקת שורה"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </td>
    </tr>
    {payOpen && (
      <CrewPaymentModal
        row={row}
        prodId={prodId}
        companyId={companyId}
        installments={installments}
        grossTotal={grossTotal}
        totalPaid={totalPaid}
        remaining={remaining}
        onClose={() => setPayOpen(false)}
        onChanged={async () => { if (onRefresh) await onRefresh(); }}
      />
    )}
    </>
  );
}

// =====================================================================
//             CREW PAYMENT MODAL  (popup launched from "+" icon)
// =====================================================================
//
// Owner spec 2026-06-14b:
//   • Opens as a LIST of payment rows ("payments to this professional").
//   • Each existing payment is shown as an editable row (amount + note + invoice).
//   • A "+ הוסף תשלום" button at the bottom of the list adds a new draft row.
//   • A single "שמור" button at the bottom saves any new draft rows in one go.
//   • Status banner shows סה״כ לתשלום / שולם / נותר for the row.
//   • Each existing payment row has its own delete button (red trash).
//   • Each existing payment row can have an invoice attached (file picker).
//   • When the running sum >= gross, the entire row UI (including the row
//     in the parent table) turns green via the derived `fullyPaid` flag.
function CrewPaymentModal({ row, prodId, companyId, installments, grossTotal, totalPaid, remaining, onClose, onChanged }) {
  const { pushUndo } = useUndo();
  const [provider, setProvider] = useState(row.provider || "");
  const [phone, setPhone] = useState(row.provider_phone || "");
  const [whatsapp, setWhatsapp] = useState(row.provider_whatsapp || row.provider_phone || "");
  // Draft rows the SA is currently entering. Each one becomes a single
  // POST to /payments on save. The shape mirrors the on-disk format so
  // the merged list is rendered uniformly.
  const [drafts, setDrafts] = useState([{ amount: "", note: "", invoice: null }]);
  const [busy, setBusy] = useState(false);
  const fmt = (n) => `₪${Number(n || 0).toFixed(0)}`;

  // Compute the live remaining as the SA types into draft rows so the
  // banner reflects what they're about to save.
  const draftsSum = drafts.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const livePaid = totalPaid + draftsSum;
  const liveRemaining = Math.max(0, grossTotal - livePaid);
  const liveFullyPaid = grossTotal > 0 && livePaid + 0.01 >= grossTotal;

  const onUpdateDraft = (idx, patch) => {
    setDrafts((arr) => arr.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };
  const onRemoveDraft = (idx) => {
    setDrafts((arr) => arr.length === 1 ? [{ amount: "", note: "", invoice: null }] : arr.filter((_, i) => i !== idx));
  };
  const onAddDraft = () => setDrafts((arr) => [...arr, { amount: "", note: "", invoice: null }]);

  const onRemoveExisting = async (pay) => {
    if (!window.confirm(`למחוק תשלום של ${fmt(pay.amount)}?`)) return;
    try {
      await deleteCrewPayment(prodId, row.id, pay.id, companyId);
      toast.success("התשלום נמחק");
      await onChanged();
      // Undo: re-create the payment with the same amount/note. Note that
      // a brand-new id is assigned by the backend on re-add, so redo will
      // delete the *new* id, not the old one.
      pushUndo({
        label: `מחיקת תשלום ${fmt(pay.amount)}`,
        undo: async () => {
          const created = await addCrewPayment(prodId, row.id, {
            amount: pay.amount, note: pay.note || "",
          }, companyId);
          pay._reCreatedId = created?.id;
          await onChanged();
        },
        redo: async () => {
          const idToDelete = pay._reCreatedId || pay.id;
          try { await deleteCrewPayment(prodId, row.id, idToDelete, companyId); } catch (_e) { /* ignore — undo is best-effort */ }
          await onChanged();
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה במחיקה");
    }
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    // Filter out empty drafts
    const toSave = drafts.filter((d) => Number(d.amount) > 0);
    if (toSave.length === 0) {
      toast.error("הזן לפחות שורת תשלום אחת");
      return;
    }
    // Overpayment guard — owner spec 2026-06-15. If the running total
    // (existing + drafts) exceeds the row's gross, surface a hard
    // confirmation popup so the SA can't accidentally over-credit the
    // supplier. They can still proceed if it's intentional.
    if (grossTotal > 0) {
      const excess = livePaid - grossTotal;
      if (excess > 0.5) {
        const ok = window.confirm(
          `שים לב: יש יתרה של ₪${excess.toFixed(0)} יותר ממה שהיית צריך לשלם לספק.\n\n` +
          `סה״כ לתשלום: ₪${grossTotal.toFixed(0)}\n` +
          `שולם עד כה: ₪${totalPaid.toFixed(0)}\n` +
          `יישמר עכשיו: ₪${draftsSum.toFixed(0)}\n` +
          `סה״כ אחרי שמירה: ₪${livePaid.toFixed(0)} (יתר תשלום של ₪${excess.toFixed(0)})\n\n` +
          `להמשיך בכל זאת?`,
        );
        if (!ok) return;
      }
    }
    setBusy(true);
    try {
      const created = [];
      for (const d of toSave) {
        const r = await addCrewPayment(prodId, row.id, {
          amount: Number(d.amount),
          note: d.note,
          provider,
          providerPhone: phone,
          providerWhatsapp: whatsapp,
          invoice: d.invoice,
        }, companyId);
        if (r?.id) created.push({ id: r.id, amount: Number(d.amount), note: d.note });
      }
      toast.success(toSave.length === 1 ? "התשלום נשמר" : `${toSave.length} תשלומים נשמרו`);
      setDrafts([{ amount: "", note: "", invoice: null }]);
      await onChanged();
      // Undo: delete each newly-created installment.
      if (created.length) {
        const labelSum = created.reduce((s, c) => s + c.amount, 0);
        pushUndo({
          label: `${created.length === 1 ? "תשלום" : created.length + " תשלומים"} ${fmt(labelSum)}`,
          undo: async () => {
            for (const c of created) {
              try { await deleteCrewPayment(prodId, row.id, c.id, companyId); } catch (_e) { /* ignore — undo is best-effort */ }
            }
            await onChanged();
          },
          redo: async () => {
            // Re-create each — get fresh ids; mutate `created` in-place so a
            // subsequent undo still works.
            for (const c of created) {
              const r = await addCrewPayment(prodId, row.id, { amount: c.amount, note: c.note || "" }, companyId);
              if (r?.id) c.id = r.id;
            }
            await onChanged();
          },
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה ברישום התשלומים");
    } finally { setBusy(false); }
  };

  return (
    <tr>
      <td colSpan={7} className="p-0">
        <div
          className="fixed inset-0 z-[10003] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="ff-crew-pay-modal"
        >
          <div
            dir="rtl"
            className="bg-stone-900 rounded-2xl border border-emerald-500/40 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between p-5 border-b border-white/10 bg-gradient-to-l from-emerald-950/40 to-stone-900 sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-400" />
                <h3 className="font-heading text-xl font-extrabold text-white">תשלומים — {provider || row.role || "איש צוות"}</h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-stone-400 hover:text-white hover:bg-white/5 rounded-lg p-1.5"
                data-testid="ff-crew-pay-close"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            {/* Status banner — auto-updates as drafts are typed */}
            <div className={`px-5 py-3 border-b border-white/10 grid grid-cols-3 gap-3 text-center text-sm ${
              liveFullyPaid
                ? "bg-emerald-950/40 text-emerald-200"
                : livePaid > 0
                ? "bg-amber-950/40 text-amber-200"
                : "bg-stone-800/40 text-stone-300"
            }`}>
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-70">סה״כ לתשלום</div>
                <div className="font-extrabold tabular-nums text-base">{fmt(grossTotal)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-70">שולם / יישמר</div>
                <div className="font-extrabold tabular-nums text-base">{fmt(livePaid)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-70">נותר</div>
                <div className="font-extrabold tabular-nums text-base">{fmt(liveRemaining)}</div>
              </div>
            </div>

            {/* Contact (one-time fields per row) */}
            <div className="p-5 border-b border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="שם איש המקצוע">
                <input type="text" value={provider} onChange={(e) => setProvider(e.target.value)}
                       className={crewPayInputCls} data-testid="ff-crew-pay-provider" placeholder="שם מלא" />
              </Field>
              <Field label="טלפון">
                <input type="tel" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)}
                       className={crewPayInputCls} data-testid="ff-crew-pay-phone" placeholder="050…" />
              </Field>
              <Field label="WhatsApp">
                <input type="tel" dir="ltr" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
                       className={crewPayInputCls} data-testid="ff-crew-pay-wa" placeholder="050…" />
              </Field>
            </div>

            {/* Existing payments list */}
            {installments.length > 0 && (
              <section className="p-5 space-y-2 border-b border-white/10" data-testid="ff-crew-pay-history">
                <h4 className="text-xs uppercase tracking-widest font-bold text-stone-400 flex items-center gap-2">
                  <Receipt className="h-3.5 w-3.5" />
                  תשלומים קודמים ({installments.length})
                </h4>
                <ul className="space-y-1.5">
                  {installments.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center gap-2 rounded-lg border border-white/5 bg-stone-800/60 px-3 py-2"
                      data-testid={`ff-crew-pay-item-${p.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-extrabold text-emerald-300 tabular-nums text-base">{fmt(p.amount)}</div>
                        <div className="text-[11px] text-stone-400 truncate">
                          {new Date(p.created_at).toLocaleDateString("he-IL")}
                          {p.note ? ` · ${p.note}` : ""}
                        </div>
                      </div>
                      {/* Invoice — view + replace (file upload) + delete */}
                      {p.invoice_url ? (
                        <>
                          <a href={resolveStorageUrl(p.invoice_url)} target="_blank" rel="noreferrer"
                             className="text-sky-300 hover:text-sky-200 p-1.5 rounded-lg hover:bg-sky-500/10"
                             data-testid={`ff-crew-pay-invoice-view-${p.id}`} title="פתח חשבונית">
                            <FileText className="h-4 w-4" />
                          </a>
                          <label className="cursor-pointer text-amber-300 hover:text-amber-200 p-1.5 rounded-lg hover:bg-amber-500/10"
                                 data-testid={`ff-crew-pay-invoice-replace-${p.id}`} title="החלף חשבונית">
                            <RotateCcw className="h-4 w-4" />
                            <input type="file" accept="image/*,application/pdf" className="hidden"
                                   onChange={async (e) => {
                                     const f = e.target.files?.[0]; if (!f) return;
                                     try {
                                       await replaceCrewPaymentInvoice(prodId, row.id, p.id, f, companyId);
                                       toast.success("החשבונית הוחלפה");
                                       await onChanged();
                                     } catch (err) { toast.error(err.response?.data?.detail || "שגיאה בהחלפת חשבונית"); }
                                     finally { e.target.value = ""; }
                                   }} />
                          </label>
                          <button type="button"
                                  onClick={async () => {
                                    if (!window.confirm("למחוק את החשבונית? התשלום יישאר.")) return;
                                    try {
                                      await replaceCrewPaymentInvoice(prodId, row.id, p.id, null, companyId, { clear: true });
                                      toast.success("החשבונית נמחקה");
                                      await onChanged();
                                    } catch (err) { toast.error(err.response?.data?.detail || "שגיאה במחיקת חשבונית"); }
                                  }}
                                  className="text-rose-300 hover:text-rose-200 p-1.5 rounded-lg hover:bg-rose-500/10"
                                  data-testid={`ff-crew-pay-invoice-delete-${p.id}`} title="מחק חשבונית בלבד">
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        // No invoice yet — offer to upload one without
                        // re-creating the payment.
                        <label className="cursor-pointer text-emerald-300 hover:text-emerald-200 p-1.5 rounded-lg hover:bg-emerald-500/10"
                               data-testid={`ff-crew-pay-invoice-add-${p.id}`} title="צרף חשבונית">
                          <Upload className="h-4 w-4" />
                          <input type="file" accept="image/*,application/pdf" className="hidden"
                                 onChange={async (e) => {
                                   const f = e.target.files?.[0]; if (!f) return;
                                   try {
                                     await replaceCrewPaymentInvoice(prodId, row.id, p.id, f, companyId);
                                     toast.success("החשבונית נוספה");
                                     await onChanged();
                                   } catch (err) { toast.error(err.response?.data?.detail || "שגיאה בהעלאת חשבונית"); }
                                   finally { e.target.value = ""; }
                                 }} />
                        </label>
                      )}
                      <button type="button" onClick={() => onRemoveExisting(p)}
                              className="text-rose-400 hover:text-rose-300 p-1.5 rounded-lg hover:bg-rose-500/10"
                              data-testid={`ff-crew-pay-delete-${p.id}`} title="מחק תשלום">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* New payments — multi-row form */}
            <form onSubmit={onSubmit} className="p-5 space-y-3">
              <h4 className="text-xs uppercase tracking-widest font-bold text-emerald-300 flex items-center gap-2">
                <Plus className="h-3.5 w-3.5" />
                רישום תשלום חדש
              </h4>
              <ul className="space-y-2" data-testid="ff-crew-pay-drafts">
                {drafts.map((d, idx) => (
                  <li key={idx} className="rounded-lg border border-white/10 bg-stone-800/40 p-2.5 space-y-2" data-testid={`ff-crew-pay-draft-${idx}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                      <div className="col-span-1 sm:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400 block mb-1">סכום (₪)</label>
                        <input type="number" step="0.01" dir="ltr" value={d.amount}
                               onChange={(e) => onUpdateDraft(idx, { amount: e.target.value })}
                               className={`${crewPayInputCls} text-right tabular-nums font-bold`}
                               data-testid={`ff-crew-pay-amount-${idx}`} min="0"
                               placeholder={idx === 0 && remaining > 0 ? remaining.toFixed(2) : "0.00"} />
                      </div>
                      <div className="col-span-1 sm:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400 block mb-1">הערה</label>
                        <input type="text" value={d.note}
                               onChange={(e) => onUpdateDraft(idx, { note: e.target.value })}
                               className={crewPayInputCls} data-testid={`ff-crew-pay-note-${idx}`}
                               placeholder="מזומן / העברה / תאריך…" />
                      </div>
                      <div className="col-span-2 sm:col-span-1 flex items-end justify-end gap-1">
                        <label className="inline-flex items-center gap-1 cursor-pointer px-2.5 py-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 text-xs font-bold"
                               data-testid={`ff-crew-pay-invoice-btn-${idx}`}>
                          <Upload className="h-3.5 w-3.5" />
                          <span>{d.invoice ? "✓" : "חשבונית"}</span>
                          <input type="file" accept="image/*,application/pdf" className="hidden"
                                 onChange={(e) => onUpdateDraft(idx, { invoice: e.target.files?.[0] || null })}
                                 data-testid={`ff-crew-pay-invoice-input-${idx}`} />
                        </label>
                        {drafts.length > 1 && (
                          <button type="button" onClick={() => onRemoveDraft(idx)}
                                  className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-lg p-2"
                                  data-testid={`ff-crew-pay-draft-remove-${idx}`} title="הסר שורה">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {d.invoice && (
                      <div className="text-[10px] text-emerald-200/80 pl-1 truncate" data-testid={`ff-crew-pay-invoice-name-${idx}`}>
                        קובץ: {d.invoice.name}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <button type="button" onClick={onAddDraft}
                      className="w-full py-2 rounded-lg border-2 border-dashed border-emerald-500/40 hover:border-emerald-400/60 hover:bg-emerald-500/5 text-emerald-300 text-sm font-bold inline-flex items-center justify-center gap-1.5 transition"
                      data-testid="ff-crew-pay-add-draft">
                <Plus className="h-4 w-4" />
                הוסף עוד תשלום
              </button>

              {/* Bottom sticky save bar */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5 mt-2">
                <button type="button" onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-white/10 bg-stone-800 hover:bg-stone-700 text-stone-200 text-sm font-bold"
                        data-testid="ff-crew-pay-cancel">
                  ביטול
                </button>
                <button type="submit" disabled={busy}
                        className="px-5 py-2 rounded-lg bg-gradient-to-l from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="ff-crew-pay-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  שמור
                </button>
              </div>
            </form>
          </div>
        </div>
      </td>
    </tr>
  );
}

const crewPayInputCls = "w-full bg-stone-800 border border-white/10 text-stone-100 font-semibold placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 rounded-lg px-3 py-2 text-sm";

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] uppercase tracking-widest font-bold text-stone-400">{label}</label>
      {children}
    </div>
  );
}

// ----------- Supplier Autocomplete combo -----------
function SupplierAutocomplete({ value, companyId, onChange, onCommit, onPick, testid }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Fetch matching suppliers (debounced light fetch).
  useEffect(() => {
    if (!open) return;
    const v = (value || "").trim();
    if (v.length < 1) { setSuggestions([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const arr = await searchSuppliers(v, companyId);
        if (active) setSuggestions(arr);
      } catch { /* silent */ }
    }, 180);
    return () => { active = false; clearTimeout(t); };
  }, [value, open, companyId]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => { setTimeout(() => onCommit(e.target.value), 120); }}
        placeholder="שם הספק"
        className="w-full bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-base"
        data-testid={testid}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-30 right-0 left-0 mt-1 max-h-56 overflow-auto rounded-lg border border-white/10 bg-stone-900 shadow-xl shadow-black/50 text-sm" data-testid={`${testid}-suggestions`}>
          {suggestions.map((s, idx) => (
            <li key={s.id || `${s.name}-${s.phone}-${idx}`}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(s); setOpen(false); }}
                className="w-full text-right px-3 py-2 hover:bg-pink-500/10 focus:bg-pink-500/10 text-stone-100 flex items-center justify-between gap-3"
              >
                <span className="font-semibold">{s.name}</span>
                {s.phone && <span dir="ltr" className="text-stone-400 text-xs tabular-nums">{s.phone}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// =============== Additional Expenses (receipt photos + OCR + Paperless WA) ===============
function AdditionalExpensesSection({ prod, companyId, paperlessPhone, onChange }) {
  const [uploading, setUploading] = useState(false);
  const rows = prod?.additional_expenses || [];

  const onPick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("יש לבחור תמונה (JPG/PNG/WEBP)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("גודל מקסימלי: 8MB");
      return;
    }
    setUploading(true);
    try {
      const row = await uploadAdditionalExpense(prod.id, file, companyId, true);
      if (row.ocr_status === "done") {
        toast.success(`זוהתה חשבונית: ${row.business_name || "ללא שם"}`);
      } else if (row.ocr_status === "failed") {
        toast.warning("התמונה נשמרה — לא הצלחנו לזהות אוטומטית, מלא ידנית");
      } else {
        toast.success("תמונה נשמרה");
      }
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בהעלאה");
    } finally {
      setUploading(false);
    }
  };

  // Add an EMPTY row the user can fill in by hand — no photo required.
  const onManual = async () => {
    setUploading(true);
    try {
      await uploadAdditionalExpense(prod.id, null, companyId, false);
      toast.success("נוספה שורה ריקה");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בהוספה");
    } finally {
      setUploading(false);
    }
  };

  const onUpdate = async (rowId, patch) => {
    const payload = {};
    if ("business_name" in patch) payload.business_name = patch.business_name || "";
    if ("business_phone" in patch) payload.business_phone = patch.business_phone || "";
    if ("product" in patch) payload.product = patch.product || "";
    if ("pre_vat" in patch) payload.pre_vat = Number(patch.pre_vat) || 0;
    if ("total_with_vat" in patch) payload.total_with_vat = Number(patch.total_with_vat) || 0;
    if ("has_vat" in patch) payload.has_vat = !!patch.has_vat;
    if ("date" in patch) payload.date = patch.date || "";
    try { await updateAdditionalExpense(prod.id, rowId, payload, companyId); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "שגיאה בעדכון"); }
  };

  const onDelete = async (rowId) => {
    if (!window.confirm("למחוק את השורה?")) return;
    try { await deleteAdditionalExpense(prod.id, rowId, companyId); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "שגיאה במחיקה"); }
  };

  return (
    <CollapsibleSection
      title="הוצאות כלליות"
      icon={Receipt}
      subtitle="העלה חשבונית מהמצלמה — המערכת תזהה ותמלא את הפרטים אוטומטית"
      testid="ff-additional-section"
      headerExtra={
        <div onClick={(e) => e.stopPropagation()}>
          <UploadButtons
            onPick={onPick}
            onManual={onManual}
            busy={uploading}
            idPrefix="ff-additional"
            testidPrefix="ff-additional"
          />
        </div>
      }
    >
      <div className="rounded-2xl border border-white/10 bg-stone-800 overflow-x-auto">
        <table className="w-full text-base min-w-[1400px]" data-testid="ff-additional-table">
          <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-stone-300">
            <tr>
              <th className="text-right px-3 py-3.5 font-semibold w-20">חשבונית</th>
              <th className="text-right px-3 py-3.5 font-semibold min-w-[180px]">שם העסק</th>
              <th className="text-right px-3 py-3.5 font-semibold w-44">טלפון</th>
              <th className="text-right px-3 py-3.5 font-semibold min-w-[140px]">מוצר/שירות</th>
              <th className="text-center px-2 py-3.5 font-semibold w-28 text-amber-200">+ מע״מ</th>
              <th className="text-right px-3 py-3.5 font-semibold w-64 min-w-[240px] text-rose-200 text-base">סה״כ + מע״מ</th>
              <th className="text-right px-3 py-3.5 font-semibold w-36">תאריך</th>
              <th className="text-right px-3 py-3.5 font-semibold w-40">פפרלס</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-stone-400 text-base">
                אין שורות. צלם או העלה חשבונית כדי להוסיף.
              </td></tr>
            )}
            {rows.map((row) => (
              <AdditionalRow
                key={row.id}
                row={row}
                paperlessPhone={paperlessPhone}
                onUpdate={(patch) => onUpdate(row.id, patch)}
                onDelete={() => onDelete(row.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
}

function AdditionalRow({ row, paperlessPhone, onUpdate, onDelete }) {
  const [draft, setDraft] = useState({
    business_name: row.business_name || "",
    business_phone: row.business_phone || "",
    product: row.product || "",
    pre_vat: row.pre_vat ?? 0,
    total_with_vat: row.total_with_vat ?? 0,
    // Default ✗ — supplier without VAT (עוסק פטור) is the common case.
    // If row.has_vat is missing on legacy data we still want explicit
    // opt-in, hence `=== true` rather than `!== false`.
    has_vat: row.has_vat === true,
    date: row.date || "",
  });
  useEffect(() => {
    setDraft({
      business_name: row.business_name || "",
      business_phone: row.business_phone || "",
      product: row.product || "",
      pre_vat: row.pre_vat ?? 0,
      total_with_vat: row.total_with_vat ?? 0,
      has_vat: row.has_vat === true,
      date: row.date || "",
    });
  }, [row.id]);

  // Plain wa.me deeplink — NO ?text= prefill. The user explicitly asked for
  // the chat to open EMPTY so they can paste the copied image and send it
  // without manually deleting a pre-filled URL.
  const waHref = paperlessPhone ? `https://wa.me/${paperlessPhone}` : "#";

  return (
    <tr data-testid={`ff-additional-row-${row.id}`} className="hover:bg-white/[0.02]">
      <td className="px-2 py-2.5">
        {row.image_url ? (
          <a href={resolveStorageUrl(row.image_url)} target="_blank" rel="noreferrer" className="block">
            <img src={resolveStorageUrl(row.image_url)} alt="חשבונית" className="h-14 w-14 rounded-lg object-cover border border-white/10 hover:border-pink-400/60 transition" />
          </a>
        ) : (
          <div className="h-14 w-14 rounded-lg bg-stone-800 border border-white/10" />
        )}
      </td>
      <td className="px-3 py-2.5">
        <input
          type="text"
          value={draft.business_name}
          onChange={(e) => setDraft({ ...draft, business_name: e.target.value })}
          onBlur={(e) => onUpdate({ business_name: e.target.value })}
          placeholder={row.ocr_status === "failed" ? "לא זוהה — מלא ידנית" : "שם העסק"}
          className="w-full bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-base"
          data-testid={`ff-additional-name-${row.id}`}
        />
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-1">
          <input
            type="tel" dir="ltr" inputMode="tel"
            value={draft.business_phone}
            onChange={(e) => setDraft({ ...draft, business_phone: e.target.value })}
            onBlur={(e) => onUpdate({ business_phone: e.target.value })}
            placeholder="050…"
            className="flex-1 min-w-0 bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-2 py-2 text-right tabular-nums text-base"
            data-testid={`ff-additional-phone-${row.id}`}
          />
          {(() => {
            const phoneIntl = toIntlPhone(draft.business_phone);
            const phoneTel = String(draft.business_phone || "").replace(/[^\d+]/g, "");
            return (
              <>
                <a
                  href={phoneIntl ? `https://wa.me/${phoneIntl}` : "#"}
                  target="_blank" rel="noreferrer"
                  onClick={(e) => { if (!phoneIntl) e.preventDefault(); }}
                  className={`p-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition ${!phoneIntl ? "opacity-30 pointer-events-none" : ""}`}
                  data-testid={`ff-additional-wa-${row.id}`}
                  aria-label="ווטסאפ"
                >
                  <WhatsAppIcon size={16} />
                </a>
                <a
                  href={phoneTel ? `tel:${phoneTel}` : "#"}
                  onClick={(e) => { if (!phoneTel) e.preventDefault(); }}
                  className={`p-1.5 rounded-lg bg-pink-500/10 border border-pink-500/30 text-pink-300 hover:bg-pink-500/20 transition ${!phoneTel ? "opacity-30 pointer-events-none" : ""}`}
                  data-testid={`ff-additional-call-${row.id}`}
                  aria-label="התקשר"
                >
                  <Phone className="h-3.5 w-3.5" />
                </a>
              </>
            );
          })()}
        </div>
      </td>
      <td className="px-3 py-2.5">
        <input
          type="text"
          value={draft.product}
          onChange={(e) => setDraft({ ...draft, product: e.target.value })}
          onBlur={(e) => onUpdate({ product: e.target.value })}
          placeholder="מוצר/שירות"
          className="w-full bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-base"
          data-testid={`ff-additional-product-${row.id}`}
        />
      </td>
      <td className="px-2 py-2.5">
        {/* 18% VAT toggle — owner's rule (2026-05-20): default ✗ (no VAT,
            עוסק פטור). When ticked, the entered total is treated as
            GROSS and pre_vat is derived as total / 1.18 so the summary's
            "החזרי מע״מ" line correctly reflects what we can reclaim. */}
        <label className="flex items-center justify-center gap-1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!draft.has_vat}
            onChange={(e) => {
              const hasVat = e.target.checked;
              const gross = Number(draft.total_with_vat) || 0;
              const net = hasVat ? Math.round((gross / 1.18) * 100) / 100 : gross;
              setDraft({ ...draft, has_vat: hasVat, pre_vat: net });
              onUpdate({ has_vat: hasVat, pre_vat: net });
            }}
            className="h-4 w-4 accent-amber-500"
            data-testid={`ff-additional-has-vat-${row.id}`}
            title='18% מע״מ — סמן ✓ עבור ספק עם מע״מ'
          />
          <span className="text-[10px] text-amber-300 font-bold whitespace-nowrap">+ מע״מ 18%</span>
        </label>
      </td>
      <td className="px-3 py-2.5">
        {/* Combined "סה"כ + מע"מ" cell — when has_vat=false the value IS
            the total (no VAT split). When ✓, pre_vat is recomputed from
            the gross input. */}
        <input
          type="number" step="0.01" dir="ltr"
          value={draft.total_with_vat}
          onChange={(e) => setDraft({ ...draft, total_with_vat: e.target.value })}
          onBlur={(e) => {
            const gross = Number(e.target.value) || 0;
            const net = draft.has_vat ? Math.round((gross / 1.18) * 100) / 100 : gross;
            onUpdate({ total_with_vat: gross, pre_vat: net, has_vat: !!draft.has_vat });
          }}
          className="w-full bg-rose-950 border-2 border-rose-500/70 text-rose-50 font-extrabold placeholder:text-rose-200/60 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-xl px-3 py-3 text-right tabular-nums text-2xl shadow-md shadow-rose-900/40"
          data-testid={`ff-additional-total-${row.id}`}
        />
      </td>
      <td className="px-3 py-2.5">
        <input
          type="date" dir="ltr"
          value={draft.date}
          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
          onBlur={(e) => onUpdate({ date: e.target.value })}
          className="w-full bg-stone-700 border border-white/15 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-base"
          data-testid={`ff-additional-date-${row.id}`}
        />
      </td>
      <td className="px-3 py-2.5">
        <PaperlessSend imageUrl={resolveStorageUrl(row.image_url)} businessName={row.business_name} waHref={waHref} disabled={!paperlessPhone} testIdBase={`ff-additional-paperless-${row.id}`} />
      </td>
      <td className="px-2 py-2.5">
        <button
          type="button"
          onClick={onDelete}
          className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition rounded-lg p-2"
          data-testid={`ff-additional-delete-${row.id}`}
          aria-label="מחק שורה"
          title="מחיקת שורה"
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
}

// =============== Payments Split Tracker (cash / credit / transfer + Paid toggle) ===============
const PAYMENT_METHODS = [
  { id: "cash", label: "מזומן", icon: Banknote, accent: "emerald" },
  { id: "credit", label: "אשראי", icon: CreditCard, accent: "sky" },
  { id: "transfer", label: "העברה בנקאית", icon: Building2, accent: "violet" },
  // Check (צ'ק) — new option per user request, 2026-02-12.
  { id: "check", label: "צ׳ק", icon: FileText, accent: "amber" },
];

function PaymentsSection({ prod, companyId, onChange }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const payments = prod?.payments || [];
  const totalPlanned = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const totalPaid = payments.reduce((s, p) => s + (p.paid ? (Number(p.amount) || 0) : 0), 0);
  // Project budget — pulled exactly from the client file. When "פלוס
  // מע״מ" is ticked the client actually owes the gross (entered ×1.18);
  // unticked = exempt dealer, gross = entered. Owner's rule
  // (2026-05-20): "כל מקום שרשום תקציב פרויקט הוא שואב את המידע מהתיק
  // לקוח". Fallback to legacy `income` for productions created before
  // `client_budget` became the canonical field.
  const enteredBudget = Number(prod?.client_budget) || Number(prod?.income) || 0;
  const addsVat = !!prod?.client_budget_includes_vat;
  const projectBudgetForPayments = enteredBudget + (addsVat ? enteredBudget * 0.18 : 0);
  const remainingFromBudget = projectBudgetForPayments - totalPaid;
  const remaining = projectBudgetForPayments - totalPlanned;

  const onAdd = async ({ method, amount, timing, scheduled_at }) => {
    setBusy(true);
    try {
      await addPayment(prod.id, {
        method,
        amount: Number(amount) || 0,
        paid: false,
        timing: timing || "immediate",
        scheduled_at: scheduled_at || null,
      }, companyId);
      setAdding(false);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בהוספה");
    } finally { setBusy(false); }
  };

  const onConfirmPaid = async (p, draftAmount, draftDirty) => {
    try {
      const patch = { paid: true, reminder_dismissed: true };
      if (draftDirty) patch.amount = Number(draftAmount) || 0;
      await updatePayment(prod.id, p.id, patch, companyId);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
  };

  const onUpdateAmount = async (p, newAmount) => {
    try { await updatePayment(prod.id, p.id, { amount: Number(newAmount) || 0 }, companyId); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
  };

  const onDelete = async (p) => {
    if (!window.confirm("למחוק את התשלום?")) return;
    try { await deletePayment(prod.id, p.id, companyId); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
  };

  // Open WhatsApp pre-written collection nudge for a SPECIFIC payment row.
  // Per user request the WhatsApp button is per-row instead of a single
  // global button — different rows usually correspond to different
  // tranches the customer should pay in different cycles.
  const onOpenWhatsAppForRow = (row) => {
    const phone = (prod?.client_whatsapp || prod?.client_phone || "").replace(/[^\d+]/g, "");
    if (!phone) { toast.error("אין מספר וואטסאפ ללקוח"); return; }
    const intl = toIntlPhone(phone);
    const amount = Number(row.amount) || 0;
    const lines = [
      "מה קורה?",
      `אשמח שתעבירו את הסכום שנותר על סך ${ils0(amount)}`,
      "",
      "תעדכן שהעברת בבקשה",
      "",
      "תודה, ואחלה יום :)",
    ];
    const url = `https://wa.me/${intl}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <CollapsibleSection
      title="תשלום מהלקוח"
      icon={CreditCard}
      subtitle="חלק את הסכום בין מזומן / אשראי / העברה / צ׳ק. בחר תזמון לכל תשלום. לחץ ✓ ברגע ששילמו."
      testid="ff-payments-section"
      headerExtra={
        <div className="flex flex-wrap items-center gap-2 sm:gap-3" onClick={(e) => e.stopPropagation()} data-testid="ff-payments-header-summary">
          <div className="rounded-lg bg-sky-950/60 border-2 border-sky-500/40 px-3 py-2 min-w-[140px] text-center">
            <div className="text-[10px] uppercase tracking-widest text-sky-300/90 font-extrabold">תקציב פרויקט</div>
            <div className="text-lg sm:text-xl font-extrabold tabular-nums text-sky-100" data-testid="ff-payments-budget">{ils0(projectBudgetForPayments)}</div>
          </div>
          <div className="rounded-lg bg-emerald-950/60 border-2 border-emerald-500/40 px-3 py-2 min-w-[140px] text-center">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300/90 font-extrabold">סה״כ שולם</div>
            <div className="text-lg sm:text-xl font-extrabold tabular-nums text-emerald-100" data-testid="ff-payments-paid">{ils0(totalPaid)}</div>
          </div>
          <div className={`rounded-lg ${remainingFromBudget > 0 ? "bg-amber-950/60 border-amber-500/40" : "bg-emerald-950/60 border-emerald-500/40"} border-2 px-3 py-2 min-w-[140px] text-center`}>
            <div className={`text-[10px] uppercase tracking-widest ${remainingFromBudget > 0 ? "text-amber-300/90" : "text-emerald-300/90"} font-extrabold`}>נותר לתשלום</div>
            <div className={`text-lg sm:text-xl font-extrabold tabular-nums ${remainingFromBudget > 0 ? "text-amber-100" : "text-emerald-100"}`} data-testid="ff-payments-remaining">
              {ils0(Math.max(0, remainingFromBudget))}
            </div>
          </div>
        </div>
      }
    >
      {payments.length === 0 && !adding && (
        <p className="text-stone-500 text-sm py-2">עוד לא חולקו תשלומים. לחץ על "הוסף תשלום" להתחיל.</p>
      )}

      {payments.length > 0 && (
        <div className="space-y-2">
          {payments.map((p) => (
            <PaymentRow
              key={p.id}
              p={p}
              onConfirm={onConfirmPaid}
              onUpdateAmount={onUpdateAmount}
              onDelete={() => onDelete(p)}
              onWhatsApp={() => onOpenWhatsAppForRow(p)}
            />
          ))}
        </div>
      )}

      {adding ? (
        <AddPaymentInline onCancel={() => setAdding(false)} onSubmit={onAdd} busy={busy} suggestedRemaining={remaining > 0 ? remaining : 0} />
      ) : (
        <Button
          onClick={() => setAdding(true)}
          variant="outline"
          className="border-white/10 bg-white/5 hover:bg-pink-500/10 hover:border-pink-500/40 text-white font-bold"
          data-testid="ff-add-payment-btn"
        >
          הוסף תשלום
        </Button>
      )}
    </CollapsibleSection>
  );
}

function PaymentRow({ p, onConfirm, onUpdateAmount, onDelete, onWhatsApp }) {
  const meta = PAYMENT_METHODS.find((m) => m.id === p.method) || PAYMENT_METHODS[0];
  const Icon = meta.icon;
  const accent = {
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    sky: "text-sky-400 bg-sky-500/10 border-sky-500/30",
    violet: "text-violet-400 bg-violet-500/10 border-violet-500/30",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  }[meta.accent];

  const [draft, setDraft] = useState(p.amount ?? 0);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setDraft(p.amount ?? 0); setDirty(false); }, [p.id, p.amount]);

  const commit = () => {
    if (!dirty) return;
    onUpdateAmount(p, draft);
    setDirty(false);
  };

  const isFuture = p.timing === "future" && p.scheduled_at;
  const scheduledLabel = isFuture
    ? new Date(p.scheduled_at).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border bg-stone-700 p-3 ${p.paid ? "border-emerald-500/40" : "border-white/10"}`} data-testid={`ff-payment-${p.id}`}>
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold ${accent}`}>
        <Icon className="h-4 w-4" />
        <span>{meta.label}</span>
      </div>
      {isFuture && (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-200 text-[11px] font-bold" data-testid={`ff-payment-scheduled-${p.id}`}>
          <Calendar className="h-3 w-3" />
          {scheduledLabel}
        </div>
      )}
      <input
        type="number" step="0.01" dir="ltr"
        value={draft}
        onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
        onBlur={commit}
        className="flex-1 min-w-[120px] bg-stone-700 border border-white/15 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-right tabular-nums text-base font-semibold"
        data-testid={`ff-payment-amount-${p.id}`}
      />
      {/* Per-row WhatsApp collect button. Hidden once paid (no need to chase). */}
      {!p.paid && (
        <button
          type="button"
          onClick={onWhatsApp}
          className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-100 transition"
          data-testid={`ff-payment-wa-${p.id}`}
          title="שלח וואטסאפ לגביית התשלום הזה"
          aria-label="שלח וואטסאפ"
        >
          <WhatsAppIcon size={18} />
        </button>
      )}
      {/* Single "אישור" button — persists any unsaved amount edit AND
          marks the payment as paid AND dismisses any future-reminder. */}
      <button
        type="button"
        onClick={() => onConfirm(p, draft, dirty)}
        disabled={p.paid}
        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold text-sm transition text-white disabled:opacity-60 disabled:cursor-default ${p.paid ? "bg-emerald-600" : `${ffGradient} hover:opacity-90`}`}
        data-testid={`ff-payment-confirm-${p.id}`}
        title={p.paid ? "תשלום אושר" : "שמור ואשר תשלום"}
      >
        <Check className="h-4 w-4" />
        {p.paid ? "אושר ✓" : "אישור"}
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition rounded-lg p-2"
        data-testid={`ff-payment-delete-${p.id}`}
        aria-label="מחק תשלום"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function AddPaymentInline({ onCancel, onSubmit, busy, suggestedRemaining }) {
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState(suggestedRemaining || "");
  const [timing, setTiming] = useState("immediate"); // "immediate" | "future"
  // datetime-local format is "YYYY-MM-DDTHH:mm". Default the future
  // picker to "now + 1 day" so the user only fine-tunes.
  const tomorrow = useMemo(() => {
    const d = new Date(Date.now() + 24 * 3600 * 1000);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  }, []);
  const [scheduledAt, setScheduledAt] = useState(tomorrow);

  const submit = () => {
    if (timing === "future" && !scheduledAt) {
      toast.error("בחר תאריך ושעה לתשלום העתידי"); return;
    }
    onSubmit({
      method,
      amount,
      timing,
      scheduled_at: timing === "future" ? new Date(scheduledAt).toISOString() : null,
    });
  };

  return (
    <div className="rounded-xl border border-pink-500/30 bg-pink-500/5 p-4 space-y-3" data-testid="ff-add-payment-form">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {PAYMENT_METHODS.map((m) => {
          const Icon = m.icon;
          const active = method === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setMethod(m.id)}
              className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg border font-bold text-sm transition ${
                active
                  ? "bg-pink-500/15 border-pink-500/50 text-pink-200"
                  : "bg-stone-700 border-white/10 text-stone-300 hover:bg-stone-800"
              }`}
              data-testid={`ff-add-payment-method-${m.id}`}
            >
              <Icon className="h-5 w-5" />
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Timing selector — immediate vs future (with date+time picker) */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setTiming("immediate")}
          className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border font-bold text-sm transition ${
            timing === "immediate" ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-200" : "bg-stone-700 border-white/10 text-stone-300 hover:bg-stone-800"
          }`}
          data-testid="ff-add-payment-timing-immediate"
        >
          <Check className="h-4 w-4" />
          תשלום מיידי
        </button>
        <button
          type="button"
          onClick={() => setTiming("future")}
          className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border font-bold text-sm transition ${
            timing === "future" ? "bg-amber-500/15 border-amber-500/50 text-amber-200" : "bg-stone-700 border-white/10 text-stone-300 hover:bg-stone-800"
          }`}
          data-testid="ff-add-payment-timing-future"
        >
          <Calendar className="h-4 w-4" />
          תשלום עתידי
        </button>
      </div>

      {timing === "future" && (
        <div className="space-y-1">
          <label className="text-[11px] text-amber-300 font-bold">תאריך ושעה לגביה</label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="w-full bg-stone-700 border border-amber-500/40 text-white font-semibold rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            data-testid="ff-add-payment-scheduled"
          />
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          type="number" step="0.01" dir="ltr"
          autoFocus
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="סכום (₪)"
          className="flex-1 bg-stone-700 border border-white/15 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-right tabular-nums text-base"
          data-testid="ff-add-payment-amount"
        />
        <Button onClick={onCancel} variant="ghost" className="text-stone-300 hover:bg-white/5" data-testid="ff-add-payment-cancel">ביטול</Button>
        <Button
          onClick={submit}
          disabled={busy}
          className={`${ffGradient} text-white font-bold`}
          data-testid="ff-add-payment-submit"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "הוסף"}
        </Button>
      </div>
    </div>
  );
}



// =====================================================================
//                   QUOTE SECTION (הצעת מחיר)
// =====================================================================
//
// User asked for an upload box under the client-email row that accepts an
// image, a PDF, OR an external link (Google Drive etc). Stored on the
// production document as `quote_url`.

function QuoteSection({ prod, companyId, onChange }) {
  const [busy, setBusy] = useState(false);
  const [linkMode, setLinkMode] = useState(false);
  const [linkVal, setLinkVal] = useState("");
  const fileInputRef = useRef(null);

  const url = prod.quote_url || "";

  const onFile = async (file) => {
    if (!file) return;
    // Accept image OR PDF — let the backend do the strict validation.
    setBusy(true);
    try {
      await uploadQuoteFile(prod.id, file, companyId);
      toast.success("הצעת המחיר נשמרה");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בהעלאה");
    } finally {
      setBusy(false);
      // Reset so picking the same file twice works.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onLinkSave = async () => {
    const v = (linkVal || "").trim();
    if (!v) { toast.error("הדבק קישור"); return; }
    setBusy(true);
    try {
      await uploadQuoteLink(prod.id, v, companyId);
      toast.success("הקישור נשמר");
      setLinkVal("");
      setLinkMode(false);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (!window.confirm("להסיר את הצעת המחיר?")) return;
    setBusy(true);
    try {
      await deleteQuote(prod.id, companyId);
      toast.success("הוסר");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-stone-700 p-4 sm:p-5 space-y-3" data-testid="ff-quote-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <FileText className="h-5 w-5 text-pink-400" />
            הצעת מחיר
          </h2>
          <p className="text-xs text-stone-300 mt-0.5">העלה תמונה / PDF, או הדבק קישור לדרייב</p>
        </div>
        {url ? (
          <div className="flex items-center gap-2">
            <a
              href={resolveStorageUrl(url)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 text-sm font-bold transition"
              data-testid="ff-quote-open"
            >
              <ExternalLink className="h-4 w-4" />
              פתח
            </a>
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              className="inline-flex items-center justify-center p-2 rounded-lg text-rose-300 hover:bg-rose-500/10 transition"
              data-testid="ff-quote-remove"
              title="הסר"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </div>

      {!url && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            id={`ff-quote-file-${prod.id}`}
            type="file"
            accept="image/*,application/pdf,.pdf"
            className="sr-only"
            onChange={(e) => onFile(e.target.files?.[0])}
            disabled={busy}
            data-testid="ff-quote-file-input"
          />
          <label
            htmlFor={`ff-quote-file-${prod.id}`}
            aria-disabled={busy}
            className={`inline-flex cursor-pointer items-center gap-1.5 px-4 py-2 rounded-lg ${ffGradient} text-white font-bold text-sm shadow-md shadow-pink-500/20 hover:opacity-90 transition ${busy ? "opacity-50 pointer-events-none" : ""}`}
            data-testid="ff-quote-upload-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4" />העלה תמונה / PDF</>}
          </label>

          {!linkMode ? (
            <button
              type="button"
              onClick={() => setLinkMode(true)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-stone-800 border border-white/15 text-stone-100 hover:bg-stone-700 hover:border-pink-500/40 transition text-sm font-bold"
              data-testid="ff-quote-link-btn"
            >
              <LinkIcon className="h-4 w-4" />
              לינק לדרייב
            </button>
          ) : (
            <div className="flex items-center gap-1.5 flex-1 min-w-[260px]">
              <input
                type="url"
                placeholder="https://drive.google.com/..."
                value={linkVal}
                dir="ltr"
                onChange={(e) => setLinkVal(e.target.value)}
                className="flex-1 rounded-lg px-3 py-2 text-sm bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50"
                data-testid="ff-quote-link-input"
              />
              <button
                type="button"
                onClick={onLinkSave}
                disabled={busy}
                className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg ${ffGradient} text-white font-bold text-sm shadow-md`}
                data-testid="ff-quote-link-save"
              >
                <Check className="h-4 w-4" />
                שמור
              </button>
              <button
                type="button"
                onClick={() => { setLinkMode(false); setLinkVal(""); }}
                className="inline-flex items-center justify-center p-2 rounded-lg text-stone-300 hover:bg-white/5"
                data-testid="ff-quote-link-cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}


// =====================================================================
//             ADDITIONAL CONTACTS SECTION (אנשי קשר)
// =====================================================================
//
// User asked for a card that lets them add up to 10 additional contacts
// for each production (crew, collaborators, vendors). Each contact has
// name + role + phone + WhatsApp + email. Inline-editable; new rows are
// appended via the [+] button on the right; deleted via trash icon.

const MAX_CONTACTS = 10;

function ContactsSection({ prod, companyId, onChange }) {
  const [busy, setBusy] = useState(false);
  const contacts = prod.additional_contacts || [];

  const onAdd = async () => {
    if (contacts.length >= MAX_CONTACTS) {
      toast.error(`מקסימום ${MAX_CONTACTS} אנשי קשר`);
      return;
    }
    setBusy(true);
    try {
      await addContact(prod.id, { name: "", role: "", phone: "", whatsapp: "", email: "" }, companyId);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-stone-700 p-4 sm:p-5 space-y-3" data-testid="ff-contacts-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-pink-400" />
            אנשי קשר נוספים
          </h2>
          <p className="text-xs text-stone-300 mt-0.5">
            {contacts.length}/{MAX_CONTACTS} — אנשי קשר עוזרים, ספקים, משתפי פעולה
          </p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          disabled={busy || contacts.length >= MAX_CONTACTS}
          className={`inline-flex items-center justify-center h-10 w-10 rounded-full ${ffGradient} text-white shadow-md shadow-pink-500/20 hover:opacity-90 transition ${busy || contacts.length >= MAX_CONTACTS ? "opacity-30 pointer-events-none" : ""}`}
          data-testid="ff-contact-add-btn"
          title="הוסף איש קשר"
        >
          <UserPlus className="h-5 w-5" />
        </button>
      </div>

      {contacts.length === 0 ? (
        <p className="text-sm text-stone-300 py-4 text-center">אין אנשי קשר. לחץ על + להוספה.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {contacts.map((c) => (
            <ContactCard
              key={c.id}
              contact={c}
              prodId={prod.id}
              companyId={companyId}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ContactCard({ contact, prodId, companyId, onChange }) {
  const [draft, setDraft] = useState({ ...contact });
  const { pushUndo } = useUndo();
  useEffect(() => { setDraft({ ...contact }); }, [contact.id]);

  const u = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const flush = async (k) => {
    if (draft[k] === contact[k]) return;
    const prevVal = contact[k];
    const nextVal = draft[k];
    const fieldLabel = { name: "שם", role: "תפקיד", phone: "טלפון", whatsapp: "WhatsApp", email: "אימייל" }[k] || k;
    try {
      await updateContact(prodId, contact.id, { [k]: nextVal }, companyId);
      onChange();
      // Push a granular field-level undo so the user can revert this single
      // edit with Ctrl+Z (or the floating Undo button). Redo re-applies it.
      // We also setDraft inside the callbacks so the visible input flips
      // immediately — without waiting on the parent refresh round-trip.
      pushUndo({
        label: `${fieldLabel}: "${(prevVal ?? "").toString().slice(0, 18) || "—"}" → "${(nextVal ?? "").toString().slice(0, 18) || "—"}"`,
        undo: async () => {
          await updateContact(prodId, contact.id, { [k]: prevVal }, companyId);
          setDraft((d) => ({ ...d, [k]: prevVal }));
          onChange();
        },
        redo: async () => {
          await updateContact(prodId, contact.id, { [k]: nextVal }, companyId);
          setDraft((d) => ({ ...d, [k]: nextVal }));
          onChange();
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בעדכון");
    }
  };

  const onDelete = async () => {
    if (!window.confirm("למחוק את איש הקשר?")) return;
    const snapshot = { ...contact };
    try {
      await deleteContact(prodId, contact.id, companyId);
      toast.success("נמחק");
      onChange();
      pushUndo({
        label: `איש קשר "${snapshot.name || "ללא שם"}"`,
        restore: async () => {
          await addContact(prodId, {
            name: snapshot.name || "",
            role: snapshot.role || "",
            phone: snapshot.phone || "",
            whatsapp: snapshot.whatsapp || "",
            email: snapshot.email || "",
          }, companyId);
          onChange();
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    }
  };

  const inputClass = "w-full rounded-md px-2.5 py-2 text-sm bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50";

  return (
    <div className="rounded-xl border border-white/10 bg-stone-800 p-3 space-y-2" data-testid={`ff-contact-card-${contact.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="grid grid-cols-2 gap-2 flex-1">
          <input
            value={draft.name || ""}
            placeholder="שם"
            onChange={(e) => u("name", e.target.value)}
            onBlur={() => flush("name")}
            className={inputClass}
            data-testid={`ff-contact-name-${contact.id}`}
          />
          <input
            value={draft.role || ""}
            placeholder="תפקיד"
            onChange={(e) => u("role", e.target.value)}
            onBlur={() => flush("role")}
            className={inputClass}
            data-testid={`ff-contact-role-${contact.id}`}
          />
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-lg p-2 transition"
          data-testid={`ff-contact-delete-${contact.id}`}
          aria-label="מחק"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input
          value={draft.phone || ""}
          dir="ltr"
          placeholder="טלפון"
          onChange={(e) => u("phone", e.target.value)}
          onBlur={() => flush("phone")}
          className={inputClass}
          data-testid={`ff-contact-phone-${contact.id}`}
        />
        <input
          value={draft.whatsapp || ""}
          dir="ltr"
          placeholder="WhatsApp"
          onChange={(e) => u("whatsapp", e.target.value)}
          onBlur={() => flush("whatsapp")}
          className={inputClass}
          data-testid={`ff-contact-whatsapp-${contact.id}`}
        />
        <input
          value={draft.email || ""}
          dir="ltr"
          type="email"
          placeholder="אימייל"
          onChange={(e) => u("email", e.target.value)}
          onBlur={() => flush("email")}
          className={inputClass}
          data-testid={`ff-contact-email-${contact.id}`}
        />
      </div>
      {/* Quick action bar — call / WA / email shortcuts for this contact */}
      <div className="flex items-center gap-1.5 pt-1">
        {(() => {
          const wa = toIntlPhone(contact.whatsapp || contact.phone);
          const items = [
            wa ? { href: `https://wa.me/${wa}`, icon: <WhatsAppIcon size={14} />, label: "WA", cls: "text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/15" } : null,
            contact.phone ? { href: `tel:${contact.phone}`, icon: <Phone className="h-3.5 w-3.5" />, label: "חיוג", cls: "text-pink-200 border-pink-500/40 hover:bg-pink-500/15" } : null,
            contact.email ? { href: `mailto:${contact.email}`, icon: <Mail className="h-3.5 w-3.5" />, label: "מייל", cls: "text-sky-200 border-sky-500/40 hover:bg-sky-500/15" } : null,
          ].filter(Boolean);
          return items.map((it, i) => (
            <a
              key={i}
              href={it.href}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border ${it.cls} text-xs font-bold transition`}
            >
              {it.icon}
              {it.label}
            </a>
          ));
        })()}
      </div>
    </div>
  );
}


// ----------- Compact action cards (3 pills) -----------
// "הצעת מחיר" + "אנשי קשר נוספים" + "צוות הפקה". The first two open
// popups (clean focus). The third toggles the inline crew/expense table.
function CompactActionCards({ quoteCount, contactsCount, crewCount, scheduleCount, crewSheetCount, showCrew, onOpenQuote, onOpenContacts, onOpenSchedule, onOpenCrewSheet, onToggleCrew }) {
  const Pill = ({ icon: Icon, title, count, action, testid, active }) => (
    <div className="rounded-2xl border border-white/10 bg-stone-700 p-4 flex items-center justify-between gap-3" data-testid={testid}>
      <h3 className="font-heading text-lg font-bold flex items-center gap-2">
        <Icon className="h-5 w-5 text-pink-400" />
        {title}
        {count > 0 && <span className="text-[10px] text-stone-300 font-bold">({count})</span>}
      </h3>
      <button
        type="button"
        onClick={action}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 text-sm font-bold transition"
        data-testid={`${testid}-action`}
      >
        {active ? <ChevronUp className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}
        {active ? "סגור" : "פתח"}
      </button>
    </div>
  );
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ff-compact-cards">
      <Pill icon={FileText} title="הצעת מחיר" count={quoteCount} action={onOpenQuote} testid="ff-card-quote" />
      <Pill icon={UserPlus} title="אנשי קשר נוספים" count={contactsCount} action={onOpenContacts} testid="ff-card-contacts" />
      <Pill icon={CalendarRange} title="לו״ז + ציוד" count={scheduleCount} action={onOpenSchedule} testid="ff-card-schedule" />
      <Pill icon={Users} title="דף קשר" count={crewSheetCount} action={onOpenCrewSheet} testid="ff-card-crew-sheet" />
      <Pill icon={Users} title="צוות הפקה" count={crewCount} action={onToggleCrew} testid="ff-card-crew" active={showCrew} />
    </section>
  );
}

// ----------- Bottom summary — 5-rubric structured summary --------------
//
// VAT semantics (audited 2026-05-20 — owner clarification):
//
//   ✓ checkbox checked   ("פלוס מע״מ 18%")
//     The entered budget is NET. We ADD 18% VAT on top.
//       net   = budget
//       vat   = budget × 0.18
//       gross = budget × 1.18    (what the client actually pays)
//     The VAT collected is part of INCOME (rubric 4 shows the gross).
//     Rubric 3 ("תשלומי מע״מ") shows the VAT we owe the tax authority.
//
//   ✗ checkbox unchecked  ("ללא מע״מ — עוסק פטור")
//     No VAT at all. The client is an exempt dealer.
//       net   = gross = budget
//       vat   = 0
//     Rubric 3 is HIDDEN entirely (no VAT obligation).
//
// Worked example: budget = 100,000
//   checked ✓ → gross 118,000 | net 100,000 | vat 18,000
//   unchecked  → gross 100,000 | net 100,000 | vat 0
// -----------------------------------------------------------------------
// Owner spec 2026-08-09 — Manual profit split tiles.
//
// Below the "סה״כ רווח נטו משוער" row we render THREE editable tiles
// (ניר דדון, דניאל חסין, Full·Frame). Whatever the SA types into each
// tile is subtracted from the estimated net profit AND accumulates on
// the dashboard's partner-earnings cards (replacing the previous
// automatic 50/50 split). Empty / zero tiles are a no-op.
// -----------------------------------------------------------------------
const PROFIT_SPLIT_PARTNERS = [
  { key: "nir",       name: "ניר דדון",     accent: "amber" },
  { key: "daniel",    name: "דניאל חסין",   accent: "violet" },
  { key: "fullframe", name: "Full·Frame",   accent: "sky" },
];

function ProfitSplitTiles({ netProfit, profitSplit, onProfitSplitChange, fmt }) {
  const [draft, setDraft] = React.useState({
    nir:       profitSplit.nir != null ? String(profitSplit.nir) : "",
    daniel:    profitSplit.daniel != null ? String(profitSplit.daniel) : "",
    fullframe: profitSplit.fullframe != null ? String(profitSplit.fullframe) : "",
  });
  React.useEffect(() => {
    setDraft({
      nir:       profitSplit.nir != null ? String(profitSplit.nir) : "",
      daniel:    profitSplit.daniel != null ? String(profitSplit.daniel) : "",
      fullframe: profitSplit.fullframe != null ? String(profitSplit.fullframe) : "",
    });
  }, [profitSplit.nir, profitSplit.daniel, profitSplit.fullframe]);

  const totalSplit = PROFIT_SPLIT_PARTNERS.reduce(
    (s, p) => s + (Number(draft[p.key]) || 0),
    0
  );
  const remaining = netProfit - totalSplit;

  const commit = (key, raw) => {
    const num = raw === "" ? 0 : (Number(raw) || 0);
    const next = { ...profitSplit, [key]: num };
    // Prune keys that are 0 so the DB stays clean.
    Object.keys(next).forEach((k) => { if (!next[k]) delete next[k]; });
    if (typeof onProfitSplitChange === "function") onProfitSplitChange(next);
  };

  const toneMap = {
    amber:  { border: "border-amber-500/50",  bg: "bg-amber-950/40",  label: "text-amber-200" },
    violet: { border: "border-violet-500/50", bg: "bg-violet-950/40", label: "text-violet-200" },
    sky:    { border: "border-sky-500/50",    bg: "bg-sky-950/40",    label: "text-sky-200" },
  };

  return (
    <div className="mt-2 space-y-2" data-testid="ff-profit-split">
      <div className="text-[10px] uppercase tracking-widest font-extrabold text-stone-300/70 text-center">
        חלוקה ידנית של הרווח
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {PROFIT_SPLIT_PARTNERS.map((p) => {
          const t = toneMap[p.accent];
          return (
            <div
              key={p.key}
              className={`rounded-lg border-2 ${t.border} ${t.bg} px-3 py-2 flex flex-col gap-1`}
              data-testid={`ff-profit-split-${p.key}`}
            >
              <div className={`text-[11px] font-extrabold ${t.label}`}>{p.name}</div>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0"
                  value={draft[p.key]}
                  onChange={(e) => setDraft({ ...draft, [p.key]: e.target.value })}
                  onBlur={(e) => commit(p.key, e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  className="flex-1 bg-stone-900/70 border border-white/10 rounded px-2 py-1 text-sm font-bold tabular-nums text-white focus:outline-none focus:border-emerald-400/60"
                  data-testid={`ff-profit-split-input-${p.key}`}
                />
                <span className="text-xs text-stone-300 font-bold">₪</span>
              </div>
            </div>
          );
        })}
      </div>
      <div
        className={`rounded-lg border px-3 py-2 flex items-center justify-between ${
          remaining >= 0
            ? "bg-emerald-950/40 border-emerald-500/40"
            : "bg-rose-950/50 border-rose-500/60"
        }`}
        data-testid="ff-profit-split-remaining"
      >
        <span className={`text-xs font-bold ${remaining >= 0 ? "text-emerald-200" : "text-rose-200"}`}>
          {remaining >= 0 ? "יתרת רווח אחרי חלוקה" : "חריגה מהרווח המשוער"}
        </span>
        <span className={`text-sm sm:text-base font-extrabold tabular-nums ${remaining >= 0 ? "text-emerald-100" : "text-rose-100"}`}>
          {fmt(remaining)}
        </span>
      </div>
    </div>
  );
}


function ProductionSummaryTable({ totals, budget, budgetIncludesVat = true, paidToDate = 0, profitSplit = {}, onProfitSplitChange }) {
  const fmt = (n) => new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(Number(n) || 0);

  const VAT_RATE = 0.18;

  const enteredBudget = Number(budget) || 0;
  // `client_budget_includes_vat` is a legacy field name; semantically it
  // now means "ADD VAT on top" (see header docblock).
  const addVat = !!budgetIncludesVat;
  const netBudget   = enteredBudget;
  const clientVat   = addVat ? enteredBudget * VAT_RATE : 0;
  const grossBudget = enteredBudget + clientVat;

  // Expense split per the 2026-05-20 rubric design:
  //   • "הוצאות כלליות" = additional_expenses array (everything that
  //     isn't crew salaries — gear rental, food, gas, etc.)
  //   • "צוות הפקה"     = expenses array (crew salaries)
  // Both arrays already store amounts gross-of-VAT in the UI.
  const crewCost      = totals.totalAmount + totals.totalVat;   // צוות הפקה
  const generalCost   = totals.additionalTotal;                  // הוצאות כלליות
  const totalExpenses = crewCost + generalCost;

  // VAT refunds (input-VAT we recover from these expenses)
  const crewVatRefund     = totals.totalVat;
  const generalVatRefund  = totals.additionalVat;
  const totalVatRefund    = crewVatRefund + generalVatRefund;

  // VAT we PAY (output-VAT collected from the client on the budget).
  // If the user did NOT tick "כולל מע״מ", there's still VAT to remit —
  // it's just stacked on top of the budget. Both modes therefore have
  // an `clientVat` figure. The "no VAT" case happens only if budget=0.
  const vatToRemit = clientVat;

  // Estimated INCOME TAX expense (25% × netBudget).
  // Owner's rule (2026-05-20): "הוצאות מס משוערת — 25% עבור סכום
  // תקציב הפרויקט". We use the NET budget (excluding VAT) because
  // Israeli income tax is always calculated on the net.
  const INCOME_TAX_RATE = 0.25;
  const incomeTaxEstimate = netBudget * INCOME_TAX_RATE;

  // Estimated TAX REFUND from invoices we received (20% × total net
  // expenses). Owner's rule: "סכום זיכוי מס משוער 20% — בנוי מקבלת
  // חשבוניות בהוצאות כלליות וצוות הפקה". The base is the NET (ex-VAT)
  // total of both expense buckets — that's the deductible portion that
  // saves us tax later.
  const TAX_REFUND_RATE = 0.20;
  const totalExpensesNet =
    (crewCost - crewVatRefund) + (generalCost - generalVatRefund);
  const taxRefundEstimate = totalExpensesNet * TAX_REFUND_RATE;

  // Owner spec 2026-06-15e — SEQUENTIAL net profit formula:
  //   netBeforeTax  = budget − expenses
  //   afterVat      = netBeforeTax × 0.82     (after 18% VAT)
  //   afterTax      = afterVat × 0.75         (after 25% tax)
  // The two deductions are no longer parallel (i.e. NOT 76,000 × 0.57).
  // The SA sees first VAT, then the resulting net, then tax on top.
  const netBeforeTax = grossBudget - totalExpenses;
  const nbtForCalc = Math.max(0, netBeforeTax);
  const estVat18 = nbtForCalc * 0.18;                  // first deduction
  const afterVat = nbtForCalc - estVat18;              // intermediate
  const estTax25 = afterVat * 0.25;                    // second deduction (on remainder)
  // For losses, the net profit stays negative — don't apply the
  // percentages to a negative base.
  const netProfit = netBeforeTax >= 0
    ? afterVat - estTax25
    : netBeforeTax;

  // Build a row — small helper
  const Row = ({ label, value, tone, testid, subnote, big }) => {
    const tc = {
      info:     { bg: "bg-sky-950/60 border-sky-600/40",         label: "text-sky-200",     value: "text-sky-100" },
      warning:  { bg: "bg-amber-950/50 border-amber-600/40",     label: "text-amber-200",   value: "text-amber-100" },
      negative: { bg: "bg-rose-950/60 border-rose-600/40",       label: "text-rose-200",    value: "text-rose-100" },
      positive: { bg: "bg-emerald-950/60 border-emerald-600/40", label: "text-emerald-200", value: "text-emerald-100" },
      muted:    { bg: "bg-stone-800/60 border-white/10",         label: "text-stone-300",   value: "text-stone-100" },
    }[tone];
    return (
      <div className={`rounded-lg border px-3 py-2.5 ${tc.bg}`} data-testid={testid}>
        <div className="flex items-center justify-between gap-2">
          <span className={`${big ? "text-sm sm:text-base" : "text-xs sm:text-sm"} font-bold ${tc.label}`}>{label}</span>
          <span className={`${big ? "text-lg sm:text-xl" : "text-sm sm:text-base"} font-extrabold tabular-nums ${tc.value}`}>{value}</span>
        </div>
        {subnote && (
          <div className="text-[10px] text-stone-400 mt-1 font-normal">{subnote}</div>
        )}
      </div>
    );
  };

  // Card wrapper
  const Card = ({ title, color, icon: Icon, children, testid, num }) => (
    <div
      className={`rounded-2xl border-2 p-3 sm:p-4 space-y-2 ${color}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1">
        {num != null && (
          <span className="h-5 w-5 rounded-full bg-white/15 text-[11px] font-extrabold flex items-center justify-center">
            {num}
          </span>
        )}
        <Icon className="h-4 w-4" />
        <span className="text-[11px] sm:text-xs uppercase tracking-widest font-extrabold">{title}</span>
      </div>
      {children}
    </div>
  );

  return (
    <CollapsibleSection
      title="סיכום פרויקט"
      icon={Banknote}
      testid="ff-summary-table"
      accent="sky"
    >
      {/* Owner spec 2026-06-15 — 4 vertical cards:
          1. תקציב פרויקט
          2. הוצאות פרויקט (כלליות + הפקה + סה״כ)
          3. תקציב − הוצאות = סה״כ נטו הכנסה לפני מס
          4. סה״כ הכנסה / מס 25% / מע״מ 18% / סה״כ רווח נטו משוער */}
      <div className="flex flex-col gap-3">

        {/* 1. תקציב פרויקט */}
        <Card
          num={1}
          title="תקציב פרויקט"
          color="bg-sky-950/30 border-sky-500/30 text-sky-200"
          icon={Wallet}
          testid="ff-rubric-1-income"
        >
          <Row
            label={addVat ? "תקציב כולל מע״מ" : "תקציב (ללא מע״מ)"}
            value={fmt(grossBudget)}
            tone="info"
            testid="ff-r1-budget"
            big
            subnote={
              <span>
                {addVat && (
                  <>
                    נטו <strong className="text-sky-300">{fmt(netBudget)}</strong>
                    {" + מע״מ "}
                    <strong className="text-amber-300">{fmt(clientVat)}</strong>
                    {" · "}
                  </>
                )}
                שולם עד כה: <strong className="text-emerald-300">{fmt(paidToDate)}</strong>
              </span>
            }
          />
        </Card>

        {/* 2. הוצאות פרויקט — פירוט + סה״כ */}
        <Card
          num={2}
          title="הוצאות פרויקט"
          color="bg-rose-950/30 border-rose-500/30 text-rose-200"
          icon={TrendingDown}
          testid="ff-rubric-2-expenses"
        >
          <Row label="הוצאות כלליות" value={fmt(generalCost)} tone="negative" testid="ff-r2-general" />
          <Row label="הוצאות הפקה (צוות)" value={fmt(crewCost)} tone="negative" testid="ff-r2-crew" />
          <Row label="סה״כ הוצאות" value={fmt(totalExpenses)} tone="negative" testid="ff-r2-total" big />
        </Card>

        {/* 3. רווח נטו משוער — owner spec 2026-06-15b: removed the
            stand-alone "הכנסה נטו לפני מס" card; the calc still happens
            inside here as the first sub-row, then 25% tax + 18% VAT,
            then the final net at the bottom.
            Owner spec 2026-06-15c: "תקציב − הוצאות" rendered as TWO
            clearer button-pills side by side (תקציב + הוצאות) with the
            resulting amount BELOW. */}
        <Card
          num={3}
          title="רווח נטו משוער"
          color={`bg-gradient-to-br ${netProfit >= 0 ? "from-emerald-950/60 to-sky-950/40 border-emerald-400/50 text-emerald-200" : "from-rose-950/60 to-stone-950 border-rose-400/50 text-rose-200"}`}
          icon={Banknote}
          testid="ff-rubric-4-net-profit"
        >
          {/* Budget vs Expenses — two compact pill-buttons, then the
              difference below them. Easier to scan than a single sub-row. */}
          <div className="grid grid-cols-2 gap-2" data-testid="ff-r4-budget-vs-expenses">
            <div className="rounded-lg border-2 border-sky-500/50 bg-sky-950/50 px-3 py-2 text-center">
              <div className="text-[10px] uppercase tracking-widest font-extrabold text-sky-300/90">תקציב</div>
              <div className="text-base sm:text-lg font-extrabold tabular-nums text-sky-100" data-testid="ff-r4-budget-pill">{fmt(grossBudget)}</div>
            </div>
            <div className="rounded-lg border-2 border-rose-500/50 bg-rose-950/50 px-3 py-2 text-center">
              <div className="text-[10px] uppercase tracking-widest font-extrabold text-rose-300/90">הוצאות</div>
              <div className="text-base sm:text-lg font-extrabold tabular-nums text-rose-100" data-testid="ff-r4-expenses-pill">{fmt(totalExpenses)}</div>
            </div>
          </div>
          <div className="text-center py-2 mt-1 rounded-lg bg-stone-900/40 border border-white/10" data-testid="ff-r4-income">
            <div className="text-[10px] uppercase tracking-widest font-extrabold text-stone-300/80">תקציב − הוצאות</div>
            <div className={`text-2xl sm:text-3xl font-extrabold tabular-nums leading-tight ${netBeforeTax >= 0 ? "text-stone-100" : "text-rose-200"}`}>{fmt(netBeforeTax)}</div>
          </div>
          {/* Sequential flow per owner spec 2026-06-15e:
              first VAT 18%, then "after VAT" intermediate panel,
              then 25% tax (computed on the after-VAT remainder),
              then the final net at the bottom. */}
          <Row label="מע״מ משוער 18%" value={`-${fmt(estVat18)}`} tone="warning" testid="ff-r4-vat-18" subnote="18% × (תקציב − הוצאות)" />
          <div className="text-center py-2 mt-1 rounded-lg bg-stone-900/40 border border-white/10" data-testid="ff-r4-after-vat">
            <div className="text-[10px] uppercase tracking-widest font-extrabold text-stone-300/80">לאחר מע״מ</div>
            <div className={`text-xl sm:text-2xl font-extrabold tabular-nums leading-tight ${afterVat >= 0 ? "text-stone-100" : "text-rose-200"}`}>{fmt(afterVat)}</div>
          </div>
          <Row label="מס משוער 25%" value={`-${fmt(estTax25)}`} tone="warning" testid="ff-r4-tax-25" subnote="25% × (לאחר מע״מ)" />
          <Row
            label={netProfit >= 0 ? "סה״כ רווח נטו משוער" : "הפסד משוער"}
            value={fmt(netProfit)}
            tone={netProfit >= 0 ? "positive" : "negative"}
            testid="ff-r4-net-profit"
            big
            subnote="= (תקציב − הוצאות) − 18% מע״מ − 25% מס על היתרה"
          />
          <ProfitSplitTiles
            netProfit={netProfit}
            profitSplit={profitSplit}
            onProfitSplitChange={onProfitSplitChange}
            fmt={fmt}
          />
        </Card>
      </div>
    </CollapsibleSection>
  );
}


// =====================================================================
//                  CREW EXPENSES SECTION (צוות הפקה)
// =====================================================================
//
// Restored after the user reported its accidental removal. Renders the
// crew/supplier expense table inline: role / supplier / phone (with WA &
// call buttons) / amount / amount + VAT / delete. Reuses the existing
// ExpenseRow component verbatim.
function CrewExpensesSection({ prod, companyId, onAdd, onUpdate, onDelete, onRefresh }) {
  const rows = prod?.expenses || [];
  return (
    <CollapsibleSection
      title="צוות הפקה"
      icon={Users}
      subtitle="תפקיד · שם · טלפון · סכום · סה״כ עם מע״מ"
      testid="ff-crew-section"
      defaultOpen={true}
      headerExtra={
        <Button
          onClick={(e) => { e.stopPropagation(); onAdd(); }}
          variant="outline"
          className="border-white/10 bg-white/5 hover:bg-pink-500/10 hover:border-pink-500/40 text-white font-bold"
          data-testid="ff-crew-add-row"
        >
          <Plus className="h-4 w-4 ml-1.5" /> הוסף שורה
        </Button>
      }
    >
      <div className="rounded-xl border border-white/10 bg-stone-800 overflow-x-auto">
        <table className="w-full text-base min-w-[920px]" data-testid="ff-crew-table">
          <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-stone-300">
            <tr>
              <th className="text-right px-3 py-3.5 font-semibold w-44">תפקיד</th>
              <th className="text-right px-3 py-3.5 font-semibold min-w-[180px]">שם</th>
              <th className="text-right px-3 py-3.5 font-semibold w-72 min-w-[280px]">טלפון</th>
              <th className="text-center px-2 py-3.5 font-semibold w-24">+ מע״מ</th>
              <th className="text-right px-3 py-3.5 font-semibold w-64 min-w-[240px] text-rose-200">עלות צוות הפקה + מע״מ</th>
              <th className="text-center px-3 py-3.5 font-semibold w-44 min-w-[180px]">שולם</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-stone-400 text-base">
                אין שורות. לחץ "הוסף שורה" להתחיל.
              </td></tr>
            )}
            {rows.map((row) => (
              <ExpenseRow
                key={row.id}
                row={row}
                prodId={prod.id}
                companyId={companyId}
                onUpdate={(patch) => onUpdate(row.id, patch)}
                onDelete={() => onDelete(row.id)}
                onRefresh={onRefresh}
              />
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
}


// =====================================================================
//                    QUOTE FILES MODAL (popup)
// =====================================================================
//
// Centered popup for managing MULTIPLE quote files / links. Per user
// request: list of files on the LEFT, "+" button to add another file or
// link, each row has rename + delete. Save button closes the popup.
function QuoteFilesModal({ prod, companyId, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [linkMode, setLinkMode] = useState(false);
  const [linkVal, setLinkVal] = useState("");
  const [linkName, setLinkName] = useState("");
  const fileInputRef = useRef(null);

  // Build a unified list: existing quote_files preferred; fallback to the
  // legacy single quote_url so users with older productions still see it.
  const files = useMemo(() => {
    const arr = (prod.quote_files || []).slice();
    if (arr.length === 0 && prod.quote_url) {
      arr.push({
        id: "legacy",
        url: prod.quote_url,
        name: prod.quote_url.split("/").pop() || "הצעת מחיר",
        kind: prod.quote_url.includes("/api/fullframe/storage/") ? "file" : "link",
      });
    }
    return arr;
  }, [prod.quote_files, prod.quote_url]);

  const onPickFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await addQuoteFile(prod.id, { file, name: file.name }, companyId);
      toast.success("הקובץ נוסף");
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בהעלאה");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onAddLink = async () => {
    const v = (linkVal || "").trim();
    if (!v) { toast.error("הדבק קישור"); return; }
    setBusy(true);
    try {
      await addQuoteFile(prod.id, { link: v, name: linkName || "קישור חיצוני" }, companyId);
      toast.success("הקישור נוסף");
      setLinkVal(""); setLinkName(""); setLinkMode(false);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    } finally { setBusy(false); }
  };

  const onRename = async (file, newName) => {
    if (!newName || newName === file.name) return;
    try {
      if (file.id === "legacy") {
        // No id — promote to a real entry by re-adding as link.
        await addQuoteFile(prod.id, { link: file.url, name: newName }, companyId);
        await deleteQuote(prod.id, companyId);
      } else {
        await renameQuoteFile(prod.id, file.id, newName, companyId);
      }
      await onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
  };

  const onDel = async (file) => {
    if (!window.confirm(`למחוק את "${file.name || "הקובץ"}"?`)) return;
    try {
      if (file.id === "legacy") {
        await deleteQuote(prod.id, companyId);
      } else {
        await deleteQuoteFile(prod.id, file.id, companyId);
      }
      toast.success("נמחק");
      await onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
  };

  return (
    <div
      className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ff-quote-modal"
    >
      <div
        dir="rtl"
        className="bg-stone-900 rounded-2xl border border-pink-500/30 w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-5 border-b border-white/10 bg-gradient-to-l from-pink-950/40 to-stone-900">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-pink-400" />
            <h3 className="font-heading text-xl font-extrabold text-white">הצעת מחיר</h3>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-lg text-stone-300 hover:bg-white/10 inline-flex items-center justify-center"
            data-testid="ff-quote-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* File list */}
          {files.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 bg-stone-800/40 p-6 text-center text-sm text-stone-400" data-testid="ff-quote-empty">
              אין קבצים. השתמש בכפתור + כדי להוסיף קובץ או קישור.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="ff-quote-list">
              {files.map((f) => (
                <QuoteFileRow key={f.id} file={f} onRename={onRename} onDelete={onDel} />
              ))}
            </ul>
          )}

          {/* Add new file / link controls */}
          <div className="rounded-xl border border-pink-500/30 bg-pink-500/5 p-3 space-y-3" data-testid="ff-quote-add-block">
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                id={`ff-quote-file-${prod.id}`}
                type="file"
                accept="image/*,application/pdf,.pdf"
                className="sr-only"
                onChange={(e) => onPickFile(e.target.files?.[0])}
                disabled={busy}
                data-testid="ff-quote-file-input"
              />
              <label
                htmlFor={`ff-quote-file-${prod.id}`}
                aria-disabled={busy}
                className={`inline-flex cursor-pointer items-center gap-1.5 px-4 py-2 rounded-lg ${ffGradient} text-white font-bold text-sm shadow-md shadow-pink-500/20 hover:opacity-90 transition ${busy ? "opacity-50 pointer-events-none" : ""}`}
                data-testid="ff-quote-modal-upload"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" />הוסף קובץ</>}
              </label>

              {!linkMode ? (
                <button
                  type="button"
                  onClick={() => setLinkMode(true)}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-stone-800 border border-white/15 text-stone-100 hover:bg-stone-700 hover:border-pink-500/40 transition text-sm font-bold"
                  data-testid="ff-quote-modal-link-btn"
                >
                  <LinkIcon className="h-4 w-4" /> לינק לדרייב
                </button>
              ) : null}
            </div>

            {linkMode && (
              <div className="space-y-2">
                <input
                  type="url"
                  placeholder="https://drive.google.com/..."
                  value={linkVal}
                  dir="ltr"
                  onChange={(e) => setLinkVal(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-sm bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50"
                  data-testid="ff-quote-modal-link-url"
                />
                <input
                  type="text"
                  placeholder="שם לזיהוי (אופציונלי)"
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-sm bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50"
                  data-testid="ff-quote-modal-link-name"
                />
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={onAddLink}
                    disabled={busy}
                    className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg ${ffGradient} text-white font-bold text-sm shadow-md`}
                    data-testid="ff-quote-modal-link-save"
                  >
                    <Check className="h-4 w-4" /> שמור קישור
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLinkMode(false); setLinkVal(""); setLinkName(""); }}
                    className="inline-flex items-center justify-center p-2 rounded-lg text-stone-300 hover:bg-white/5"
                    data-testid="ff-quote-modal-link-cancel"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 p-4 border-t border-white/10 bg-stone-900">
          <Button
            onClick={onClose}
            className={`${ffGradient} text-white font-bold`}
            data-testid="ff-quote-modal-save"
          >
            <Save className="h-4 w-4 ml-1.5" /> שמור וסגור
          </Button>
        </footer>
      </div>
    </div>
  );
}

function QuoteFileRow({ file, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(file.name || "");
  useEffect(() => { setDraft(file.name || ""); }, [file.id, file.name]);

  const save = async () => {
    const trimmed = (draft || "").trim();
    if (trimmed) await onRename(file, trimmed);
    setEditing(false);
  };

  return (
    <li className="rounded-xl border border-white/10 bg-stone-800 p-3 flex items-center justify-between gap-3" data-testid={`ff-quote-row-${file.id}`}>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {file.kind === "link" ? <LinkIcon className="h-4 w-4 text-pink-400 flex-shrink-0" /> : <FileText className="h-4 w-4 text-pink-400 flex-shrink-0" />}
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(file.name || ""); setEditing(false); } }}
            className="flex-1 min-w-0 rounded-md px-2 py-1 text-sm bg-stone-700 border border-pink-500/40 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/50"
            data-testid={`ff-quote-rename-${file.id}`}
          />
        ) : (
          <span className="text-white font-bold truncate" title={file.name}>{file.name || "ללא שם"}</span>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <a
          href={resolveStorageUrl(file.url)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 text-xs font-bold transition"
          data-testid={`ff-quote-open-${file.id}`}
        >
          <ExternalLink className="h-3.5 w-3.5" /> פתח
        </a>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-pink-300 hover:text-pink-200 p-1.5 rounded-lg hover:bg-pink-500/10 transition"
          data-testid={`ff-quote-edit-${file.id}`}
          title="ערוך שם"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(file)}
          className="text-rose-300 hover:text-rose-200 p-1.5 rounded-lg hover:bg-rose-500/10 transition"
          data-testid={`ff-quote-delete-${file.id}`}
          title="מחק"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}


// =====================================================================
//              ADDITIONAL CONTACTS MODAL (popup wrapper)
// =====================================================================
//
// Renders the existing ContactsSection in a centered popup with a top
// header + "שמור וסגור" button. Per user request: clicking the
// "אנשי קשר נוספים" card opens this popup instead of expanding inline.
function ContactsModal({ prod, companyId, onClose, onChanged }) {
  return (
    <div
      className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ff-contacts-modal"
    >
      <div
        dir="rtl"
        className="bg-stone-900 rounded-2xl border border-pink-500/30 w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-5 border-b border-white/10 bg-gradient-to-l from-pink-950/40 to-stone-900">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-pink-400" />
            <h3 className="font-heading text-xl font-extrabold text-white">אנשי קשר נוספים</h3>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-lg text-stone-300 hover:bg-white/10 inline-flex items-center justify-center"
            data-testid="ff-contacts-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          <ContactsSection prod={prod} companyId={companyId} onChange={onChanged} />
        </div>

        <footer className="flex items-center justify-end gap-2 p-4 border-t border-white/10 bg-stone-900">
          <Button
            onClick={onClose}
            className={`${ffGradient} text-white font-bold`}
            data-testid="ff-contacts-modal-save"
          >
            <Save className="h-4 w-4 ml-1.5" /> שמור וסגור
          </Button>
        </footer>
      </div>
    </div>
  );
}
